// F1 · Registro del caso — Formulario de notificación de efecto adverso ("Formato Verde").
// Implementa la especificacion de campos del AF §7b: 8 secciones, logica condicional para el
// caso con afectacion a personas (dato L2), seccion repetible de medicamentos y autoguardado.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Save, Send, Plus, Trash2, Paperclip, ChevronDown, ChevronUp, ChevronRight, CircleCheck,
  CircleAlert, Circle, TriangleAlert, FileWarning,
  Bird, PawPrint, Rabbit, Dog, Cat, User, Beef, CircleEllipsis, Stethoscope, Loader2,
} from 'lucide-react';
import {
  PageHeader, Field, Input, Textarea, Select, Checkbox, Radio, Button,
  Alert, Codigo, StatusBadge, Mono, L2Tag, Modal,
} from '../components/ui';
import { useCatalogos, useZonaDelPais } from '../lib/CatalogosContexto';
import { comoHora, fechaDeHoyEnZona, fechaIsoDeInstante } from '../lib/fechas';
import { comportamientoDelDesplazamiento, useSeccionEnLectura } from '../lib/useSeccionEnLectura';
import { datosEnRiesgoAlConmutar, normalizarCorreo, revisarCorreo, revisarFechaDeInicio } from '../lib/validacion';
import {
  alfanumerico, conTope, pesoDecimal, soloCorreo, soloEnteros, soloIniciales,
  soloLetras, telefono,
} from '../lib/entradas';
import { useValidacionCampos } from '../lib/useValidacionCampos';
// El formato de los MB sale de un solo sitio, el mismo que usan los mensajes de
// rechazo: dos redondeos distintos del mismo archivo confunden mas que ayudan.
import { BYTES_POR_MB, enMb } from '../lib/huella';
import SelectorDePais from '../components/ui/SelectorDePais';
import { ICONO_CATEGORIA, FormatoVerde, Vial } from '../components/Iconos';
import type { ChangeEvent, ReactNode } from 'react';
import { aFallo } from '../api/cliente';
import type { UsuarioSimulado } from '../lib/sesion';
import { registrosDeLaOpcion, useMaestroDelPais } from '../lib/useMaestroDelPais';
import { opcionesConRetirado, retiradoDe } from '../lib/valoresRetirados';
import type { ValorRetirado } from '../lib/valoresRetirados';
import { ROTULO_DATO_DE_PRUEBA } from '../lib/etiquetas';
import type {
  Adjunto, AnimalDelFormulario, CasoDelFormulario, ConfiguracionDeAdjuntos,
  EvidenciaDelFormulario, Faltante,
} from '../tipos';
import type { Icono } from '../components/ui';
import type { OpcionDeFormulario } from '../lib/CatalogosContexto';

/**
 * EL CONTENEDOR QUE DE VERDAD SE DESPLAZA, subiendo desde `el`.
 *
 * ⚠ NO es `window`. `AppShell` monta la aplicacion en `h-screen overflow-hidden` y el
 * unico elemento con desplazamiento propio es su `<main className="flex-1 overflow-y-auto">`.
 * Medido en el navegador: `document.documentElement.scrollHeight` = 900 (el alto exacto
 * de la ventana, o sea nada que desplazar) frente a `main.scrollHeight` = 3736. Por eso
 * cualquier `window.scrollBy` / `window.scrollTo` de este formulario es un no-op, y por
 * eso esto se busca en lugar de darlo por sabido: si manana el armazon cambia y el
 * scroller pasa a ser otro, esto lo encuentra igual.
 *
 * Se comprueba `overflowY` Y ADEMAS que haya algo que desplazar: un contenedor con
 * `overflow-y: auto` cuyo contenido cabe entero no desplaza, y devolverlo haria que la
 * compensacion escribiera en un `scrollTop` que se queda en 0.
 *
 * Devuelve `null` si no encuentra ninguno —en jsdom es lo normal— y quien llama no hace
 * nada, que es la conducta correcta cuando no hay nada que compensar.
 */
const contenedorDesplazable = (el: Element | null | undefined): HTMLElement | null => {
  if (!el?.parentElement || typeof getComputedStyle !== 'function') return null;
  for (let n: HTMLElement | null = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const y = getComputedStyle(n).overflowY;
    if ((y === 'auto' || y === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
};

// Seccion plegable del formulario.
//
// IMPORTANTE: va a nivel de modulo, NO dentro de RegistroCaso. Definir un componente dentro de
// otro le da una identidad nueva en cada render; React lo trata como un componente distinto,
// desmonta todo el subarbol y lo vuelve a montar. El sintoma es que el input pierde el foco a
// cada letra que se escribe. Todo el estado que necesita llega por `ctx`.
// El disco con el numero de la seccion, en la cabecera del acordeon. La PRIORIDAD es
// la regla —error, luego sin pendientes, luego pendiente— y como cadena de ternarios
// dentro del `className` quedaba escondida entre las clases de Tailwind.
const fondoDelNumeroDeSeccion = (err: boolean, ok: boolean): string => {
  if (err) return 'bg-rose-700 text-white';
  if (ok) return 'bg-marca text-white';
  return 'bg-slate-200 text-slate-700';
};

// UNA ENTRADA DEL INDICE DE SECCIONES (la columna izquierda del formulario).
//
// A nivel de modulo, como `Seccion`, y por la misma razon tecnica. Estaba escrita
// dentro del `map` del JSX y ahi acumulaba cuatro cadenas de ternarios —icono, color
// del icono, clases del boton y color del contador—: mas de la mitad de los ternarios
// anidados del archivo vivian en veinte lineas.
//
// Cada decision es ahora una funcion con nombre y con su prioridad escrita en el
// orden de los `if`, que es lo unico que hay que entender de este bloque.
const iconoDelIndice = (off?: boolean, err?: boolean, faltan?: number): Icono => {
  if (off) return Circle;
  if (err) return CircleAlert;
  if (faltan) return Circle;
  return CircleCheck;
};

const colorDelIconoDelIndice = (off?: boolean, err?: boolean, faltan?: number): string => {
  if (err) return 'text-rose-500';
  // Una seccion que no aplica y una con pendientes se pintan igual de apagadas: no
  // hay nada que celebrar en ninguna de las dos.
  if (off || faltan) return 'text-slate-300';
  return 'text-emerald-500';
};

// La ACTIVA es la seccion que se esta leyendo (ScrollSpy, `useSeccionEnLectura`): fondo de
// marca suave, barra de marca a la izquierda (la pinta `ItemDelIndice`) y texto semibold, el
// mismo patron que el menu principal. Las demas responden al cursor (Tailwind 4 ya no pone
// la mano en los botones).
const clasesDelBotonDelIndice = (off?: boolean, activa?: boolean): string => {
  if (off) return 'text-slate-300 cursor-not-allowed';
  if (activa) return 'bg-marca/10 text-ink font-semibold cursor-pointer';
  return 'text-ink-soft hover:bg-canvas hover:text-ink cursor-pointer';
};

/**
 * Una seccion del formulario. `off` marca las que NO aplican a este caso -la de la
 * persona afectada cuando no se declaro afectacion- y por eso se pintan apagadas y no
 * cuentan pendientes.
 */
type SeccionDelFormulario = {
  key: string;
  n: number;
  label: string;
  off?: boolean;
  /** La seccion contiene dato sensible de salud y se rotula como tal. */
  l2?: boolean;
};

function ItemDelIndice({ s, faltan, intentoEnvio, activa, onIr }: {
  s: SeccionDelFormulario;
  /** Cuantos pendientes tiene la seccion. */
  faltan: number;
  intentoEnvio: boolean;
  activa: boolean;
  onIr: (k: string) => void;
}) {
  const err = intentoEnvio && faltan > 0;
  const Icon = iconoDelIndice(s.off, err, faltan);
  return (
    // `aria-current="location"`: el lector de pantalla dice cual es la seccion en lectura.
    <button type="button" onClick={() => onIr(s.key)} disabled={s.off}
      aria-current={activa ? 'location' : undefined} data-testid={`registro-indice-${s.key}`}
      className={'relative w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-meta text-left transition-colors ' +
        clasesDelBotonDelIndice(s.off, activa)}>
      {activa && <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r bg-marca" />}
      <Icon size={13} className={colorDelIconoDelIndice(s.off, err, faltan)} />
      <span className="flex-1 min-w-0 leading-snug">{s.label}</span>
      {/* El contador tambien en el indice: es donde se mira para decidir a que
          seccion volver, y sin el hay que abrirlas una a una. */}
      {!s.off && faltan > 0 && (
        <span className={'text-micro font-semibold px-1.5 rounded-md tabular-nums ' +
          (err ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-ink-soft')}>{faltan}</span>
      )}
    </button>
  );
}

// UNA FILA DEL "RESUMEN ANTES DE ENVIAR" (el pie del formulario).
//
// Dice el ESTADO de cada seccion y navega a ella; no repite los mensajes, que ya
// estan arriba. Sale del JSX por lo mismo que el indice: aqui vivian otros cuatro
// ternarios anidados, y el disco del numero y la etiqueta de estado son dos escaleras
// de prioridad que se leen mucho mejor como `if` con nombre.
const fondoDelNumeroEnResumen = (off?: boolean, err?: boolean, faltan?: number): string => {
  if (off) return 'bg-slate-100 text-slate-600';
  if (err) return 'bg-rose-700 text-white';
  if (faltan) return 'bg-slate-200 text-slate-700';
  return 'bg-marca text-white';
};

// Mismo rotulo que la cabecera de la seccion, y por lo mismo: dos palabras distintas
// para el mismo estado se leen como dos estados distintos (T-44 §4).
function EstadoDeLaSeccion({ off, err, faltan }: { off?: boolean; err: boolean; faltan: number }) {
  if (off) return <StatusBadge tone="neutral" dot={false}>No aplica</StatusBadge>;
  if (faltan === 0) return <StatusBadge tone="success" icon={CircleCheck}>Sin pendientes</StatusBadge>;
  if (err) return <StatusBadge tone="danger" icon={TriangleAlert}>Faltan {faltan}</StatusBadge>;
  return <StatusBadge tone="neutral" dot={false}>{faltan} por completar</StatusBadge>;
}

function FilaDelResumen({ s, faltan, intentoEnvio, onIr }: {
  s: SeccionDelFormulario;
  faltan: number;
  intentoEnvio: boolean;
  onIr: (k: string) => void;
}) {
  const err = intentoEnvio && faltan > 0;
  return (
    <li>
      <button type="button" onClick={() => onIr(s.key)} disabled={s.off}
        className={'w-full flex flex-wrap items-center gap-3 px-5 py-2.5 text-left transition-colors ' +
          (s.off ? 'cursor-not-allowed' : 'hover:bg-canvas')}>
        <span className={'text-micro font-semibold tabular-nums w-5 h-5 rounded-md flex items-center justify-center shrink-0 ' +
          fondoDelNumeroEnResumen(s.off, err, faltan)}>{s.n}</span>
        {/* Sin `truncate`: "Descripción del paciente" se cortaba en la columna lateral
            (reportado 3-sep-2026). El nombre completo importa mas que la fila de una linea. */}
        <span className={'text-body flex-1 min-w-0 leading-snug ' + (s.off ? 'text-ink-soft' : 'text-ink')}>{s.label}</span>
        {s.l2 && <L2Tag />}
        <EstadoDeLaSeccion off={s.off} err={err} faltan={faltan} />
        {/* HU-51: la fila lleva a su seccion, y el chevron lo dice a la vista (antes solo
            el texto la distinguia de una fila informativa). */}
        {!s.off && <ChevronRight size={15} className="text-ink-soft shrink-0" aria-hidden="true" />}
      </button>
    </li>
  );
}

/**
 * La forma de pantalla de un adjunto del servidor.
 *
 * Traduce los nombres del API (`nombre_archivo`, `huella_sha256`…) a los que usa la
 * lista. Con subida simulada no hay nada que traducir: la lista local ya tiene esta
 * forma porque la escribio la propia pantalla.
 */
const evidenciasVisibles = (
  subidaReal: boolean,
  delServidor: Adjunto[] | undefined,
  locales: EvidenciaDelFormulario[],
  zona: string | null,
): EvidenciaDelFormulario[] => {
  if (!subidaReal) return locales;
  return (delServidor ?? []).map((a) => ({
    nombre: a.nombre_archivo,
    huella: a.huella_sha256,
    tipo: (a as Adjunto & { tipo_evidencia?: string }).tipo_evidencia ?? 'Evidencia',
    // RN-82: el día de la subida, en la zona del país del caso (no el día UTC del instante).
    fecha: a.creado_en ? fechaIsoDeInstante(a.creado_en, zona) : fechaDeHoyEnZona(zona),
    l2: a.contiene_dato_de_personas ?? false,
    // Para el contador de presupuesto (CP-07-08/11). El modo prototipo no sube nada
    // y por eso sus filas no lo traen: alli el contador se queda en 0, que es cierto.
    tamanoBytes: a.tamano_bytes ?? 0,
  }));
};

/**
 * Las evidencias adjuntas de la seccion 6, o lo que hay que decir cuando no hay.
 *
 * Tres estados excluyentes —lista, error, invitacion— que estaban escritos como dos
 * ternarios encadenados dentro del JSX. Como componente, cada estado es un `return` y
 * se ve de un vistazo cual gana: primero lo que hay, luego lo que falta, y solo si no
 * toca ninguna de las dos, la explicacion de que se espera.
 *
 * SE PUEDEN QUITAR SIEMPRE (CP-07-06/13/24/25, 3-sep-2026).
 *
 * Aqui decia que `sePuedenQuitar` era `false` con subida real porque "quitar un adjunto
 * es una operacion que HU-07 no entrega en el Sprint 1". Era incorrecto: HU-07 tiene el
 * escenario "Eliminar un adjunto antes de enviar", su cuadro de que puede salir mal lo
 * repite, y el servidor lo entrego con su asiento de auditoria. Lo unico que faltaba era
 * cablearlo, y sin el boton habia seis casos de prueba bloqueados.
 *
 * Con subida simulada la fila sale de la lista local; con subida real se llama al
 * servidor y la lista se refresca desde su respuesta.
 */
/**
 * HU-07 · los limites vigentes y lo que va consumido (CP-07-08 y CP-07-11, 4-sep-2026).
 *
 * La HU pide dos cosas distintas y solo una estaba: "la pantalla MUESTRA LOS LIMITES
 * VIGENTES leyendolos del servidor" se cumplia, pero "el mensaje DICE CUANTO QUEDA,
 * para que se vea venir" no, porque no habia consumo en ninguna parte.
 *
 * Y habia algo peor, que es lo que QA reporto: el parrafo de los limites vivia dentro
 * de la rama de "todavia no hay evidencias", asi que DESAPARECIA al adjuntar el primer
 * archivo. Justo cuando el consumo empieza a importar, dejaba de verse. Con 95 MB
 * cargados —el escenario de CP-07-11— no habia nada que mirar en la pantalla.
 *
 * Ahora se pinta siempre que haya limites, haya o no evidencias.
 */
function PresupuestoDeEvidencias({ evidencias, limites }: {
  evidencias: EvidenciaDelFormulario[];
  limites?: ConfiguracionDeAdjuntos | null;
}) {
  // Sin configuracion no hay presupuesto que mostrar: el bloque entero sobra, en vez
  // de pintar "Maximo undefined archivos".
  if (!limites) return null;
  const usados = evidencias.reduce((suma, ev) => suma + (ev.tamanoBytes ?? 0), 0);
  const tope = limites.tamano_total_maximo_mb * BYTES_POR_MB;
  return (
    <p className="mt-2 text-meta text-ink-soft">
      Máximo <b>{limites.cantidad_maxima}</b> archivos, <b>{limites.tamano_maximo_mb} MB</b> cada
      uno y <b>{limites.tamano_total_maximo_mb} MB</b> en total. Llevas{' '}
      <b>{evidencias.length} de {limites.cantidad_maxima}</b> archivos y <b>{enMb(usados)}</b>{' '}
      usados; quedan <b>{enMb(Math.max(0, tope - usados))}</b>.
    </p>
  );
}

function ListaDeEvidencias({ evidencias, sePuedenQuitar, onQuitar, error, limites, quitando, presupuestoDelServidor = null }: {
  evidencias: EvidenciaDelFormulario[];
  sePuedenQuitar: boolean;
  /** Recibe el INDICE de la fila en la lista. */
  onQuitar?: (indice: number) => void;
  error?: ReactNode;
  limites?: ConfiguracionDeAdjuntos | null;
  /** La huella del adjunto que se esta borrando ahora mismo, o `null`. Apaga el tacho. */
  quitando?: string | null;
  /** #377-C1: el «6 MB de 100 MB» del ultimo lote. Manda sobre el que arma la pantalla. */
  presupuestoDelServidor?: string | null;
}) {
  let presupuesto: ReactNode = null;
  if (presupuestoDelServidor) {
    presupuesto = <p className="mt-2 text-meta text-ink-soft" data-testid="registro-evidencias-presupuesto">{presupuestoDelServidor}</p>;
  } else if (limites) {
    presupuesto = <PresupuestoDeEvidencias evidencias={evidencias} limites={limites} />;
  }

  if (evidencias.length) {
    return (
      <>
        <ul className="space-y-1.5">
        {evidencias.map((ev, i) => (
          <li key={ev.huella ?? i} data-testid="registro-evidencias-fila"
            className="flex items-center justify-between gap-2 px-3 py-2 bg-canvas border border-hairline rounded-xl">
            <span className="flex items-center gap-2 text-meta text-ink min-w-0">
              <Paperclip size={13} className="text-ink-soft shrink-0" />
              <span className="min-w-0 break-all">{ev.nombre}</span>
              {ev.l2 && <L2Tag>L2</L2Tag>}
            </span>
            {sePuedenQuitar && (
              /* ⚠ SE APAGA MIENTRAS BORRA (operador, 9-sep-2026). Sin esto, tres clics
                 disparaban tres `DELETE`: el primero borra y los otros dos vuelven a
                 guardar el caso, subiendo `version_bloqueo` una vez por clic hasta que el
                 autoguardado chocaba con un 409. */
              <button type="button" onClick={() => onQuitar?.(i)}
                disabled={Boolean(quitando)}
                className="p-1 rounded text-ink-soft hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Quitar adjunto"><Trash2 size={13} /></button>
            )}
          </li>
        ))}
        </ul>
        {presupuesto}
      </>
    );
  }
  if (error) {
    return (
      <>
        <Alert tone="danger">{error}</Alert>
        {presupuesto}
      </>
    );
  }
  return (
    <>
      <p className="text-meta text-ink-soft" data-testid="registro-evidencias-vacio">
        Estudios, informes de necropsia, fotografías. <b>Al menos uno es obligatorio para
        enviar</b>.
      </p>
      {presupuesto}
    </>
  );
}

// "Falta 1 dato" / "Faltan N datos". En un solo sitio porque se dice DOS veces —en el
// resumen de arriba y en el veredicto del pie— y dos redacciones del mismo conteo se
// leen como dos cuentas distintas.
const tituloDeFaltantes = (cuantos: number): string => (cuantos === 1
  ? 'Falta 1 dato para poder enviar'
  : `Faltan ${cuantos} datos para poder enviar`);

/**
 * El veredicto del pie: se puede enviar, o falta esto.
 *
 * `intentoEnvio` decide el TONO y no el contenido: antes de pulsar enviar la lista de
 * pendientes orienta (info); despues de pulsar, acusa (danger). Es la misma regla que
 * la cabecera de cada seccion — orientar antes, avisar despues.
 */
function VeredictoDeEnvio({ completo, intentoEnvio, cuantosFaltan }: {
  completo: boolean;
  intentoEnvio: boolean;
  cuantosFaltan: number;
}) {
  if (completo) {
    return (
      <Alert tone="success" title="No falta ningún dato obligatorio">
        Puedes enviar el caso. Al enviarlo se asigna el número, se abre el expediente y se
        avisa al Comité: después ya no podrás editar el formulario.
      </Alert>
    );
  }
  return (
    <Alert tone={intentoEnvio ? 'danger' : 'info'} title={tituloDeFaltantes(cuantosFaltan)}>
      Puedes guardar el borrador incompleto, pero no enviarlo hasta completar estos datos.
    </Alert>
  );
}

/**
 * Lo que se dice cuando una seccion repetible esta VACIA: el error si ya toca
 * ensenarlo, y si no, la frase que explica que se espera ahi.
 *
 * Son animales y medicamentos, con el mismo patron y las mismas dos ramas; escribirlo
 * dos veces en el JSX era repetir un ternario y arriesgarse a que un dia dijeran cosas
 * distintas ante el mismo estado.
 */
function SeccionRepetibleVacia({ error, invitacion }: { error?: ReactNode; invitacion: ReactNode }) {
  if (error) return <div className="mb-3"><Alert tone="danger">{error}</Alert></div>;
  return <p className="text-meta text-ink-soft mb-3">{invitacion}</p>;
}

/**
 * Lo que cada seccion necesita del formulario para pintarse y validarse.
 *
 * Va como un solo objeto y no como ocho props sueltas porque las nueve secciones lo
 * necesitan ENTERO: pasarlas una a una son ocho lineas repetidas nueve veces, y basta
 * olvidar una para que una seccion deje de resaltar su campo con error sin que se note.
 */
type ContextoDeSeccion = {
  /** Que secciones estan desplegadas. */
  abierta: Set<string>;
  alternar: (k: string) => void;
  intentoEnvio: boolean;
  /** Los faltantes agrupados por clave de seccion. */
  porSeccion: Record<string, Faltante[]>;
  refs: { current: Record<string, HTMLElement | null> };
  irACampo: (f: Faltante) => void;
};

function Seccion({ sk, ctx, children }: { sk: SeccionDelFormulario; ctx: ContextoDeSeccion; children?: ReactNode }) {
  const { abierta, alternar, intentoEnvio, porSeccion, refs, irACampo } = ctx;
  const abierto = abierta.has(sk.key);
  const faltan = porSeccion[sk.key] || [];
  // ANTES de intentar enviar, lo que falta se cuenta pero no se acusa: la cabecera lleva
  // un contador neutro ("3 por completar") que sirve de mapa. DESPUES de pulsar enviar,
  // lo mismo pasa a rojo. Es la diferencia entre orientar y regañar.
  const err = intentoEnvio && faltan.length > 0;
  const ok = faltan.length === 0;
  return (
    <div ref={(el) => (refs.current[sk.key] = el)} className="scroll-mt-4">
      <div className={'bg-white border rounded-2xl overflow-hidden ' + (err ? 'border-rose-300' : 'border-hairline')}>
        {/* `data-cabecera-seccion`: el colapso automatico la ignora. Plegar o desplegar
            NO es "avanzar a la siguiente seccion" — ver el manejador del foco. */}
        {/* FOCO VISIBLE HACIA ADENTRO (reportado por pruebas de accesibilidad, 2-sep-2026).
            El contorno global de `:focus-visible` se dibuja 2px POR FUERA del control, y
            esta cabecera ocupa todo el ancho de una caja con `overflow-hidden`: la caja lo
            recortaba y la cabecera era alcanzable con Tab pero invisible al foco. WCAG 2.4.7.
            Se cambia por un anillo interior solo aqui; la regla global sigue igual. */}
        <button type="button" data-cabecera-seccion onClick={() => alternar(sk.key)} aria-expanded={abierto}
          className={'w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-marca ' +
            (sk.l2 ? 'bg-rose-50/60' : 'bg-canvas') + ' hover:brightness-[0.98]'}>
          <span className="flex items-center gap-2.5 min-w-0">
            <span className={'text-micro font-semibold tabular-nums w-5 h-5 rounded-md flex items-center justify-center shrink-0 ' +
              fondoDelNumeroDeSeccion(err, ok)}>{sk.n}</span>
            <span className="text-body font-semibold text-ink break-words min-w-0">{sk.label}</span>
            {sk.l2 && <L2Tag />}
            {err && <StatusBadge tone="danger" icon={TriangleAlert}>Revisar · {faltan.length}</StatusBadge>}
            {/* "Sin pendientes" y no "Completa" (T-44 §4, queja del equipo): lo unico
                que este contador sabe es que NO faltan obligatorios. Decir "Completa"
                con la mitad de los opcionales en blanco afirma algo que nadie
                comprobo, y el registrador que se fia de la etiqueta envia el caso sin
                el lote, sin la via y sin el estado de salud previo. */}
            {!err && !sk.off && (ok
              ? <StatusBadge tone="success" icon={CircleCheck}>Sin pendientes</StatusBadge>
              : <StatusBadge tone="neutral" dot={false}>{faltan.length} por completar</StatusBadge>)}
          </span>
          {abierto ? <ChevronUp size={16} className="text-ink-soft shrink-0" /> : <ChevronDown size={16} className="text-ink-soft shrink-0" />}
        </button>
        {abierto && (
          <div className="p-5 border-t border-hairline animate-fade-in">
            {err && (
              <div className="mb-4">
                <Alert tone="danger" title={faltan.length === 1 ? 'Falta 1 dato en esta sección' : `Faltan ${faltan.length} datos en esta sección`}>
                  <ul className="mt-1 space-y-0.5">
                    {faltan.map((x: Faltante) => (
                      <li key={x.campo}>
                        <button type="button" onClick={() => irACampo(x)} className="underline hover:no-underline text-left min-h-6 py-0.5">{x.mensaje}</button>
                      </li>
                    ))}
                  </ul>
                </Alert>
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// Icono por especie: el mismo patron de las banderas, aplicado a otro catalogo.
// Claves por CODIGO de catalogo (no por etiqueta): es lo que los Selects guardan
// desde que los tres catalogos pasaron a {id, label} (27-ago-2026).
const ICONO_ESPECIE: Record<string, Icono> = {
  aves: Bird, cerdos: PawPrint, bovinos: Beef, ovinos: PawPrint, caprinos: PawPrint,
  equinos: Rabbit, caninos: Dog, felinos: Cat, humano: User,
};
const iconoEspecie = (n: string) => {
  const I = ICONO_ESPECIE[n] || PawPrint;
  return <I size={16} className="text-ink-soft shrink-0" strokeWidth={2} />;
};

// Icono por rol: quien reporta y quien administro el medicamento comparten catalogo.
const ICONO_ROL: Record<string, Icono> = { veterinario: Stethoscope, propietario: User, otro: CircleEllipsis };
const iconoRol = (n: string) => {
  const I = ICONO_ROL[n] || CircleEllipsis;
  return <I size={16} className="text-ink-soft shrink-0" strokeWidth={2} />;
};

// HU-06: "marco exposicion por inyeccion -> se abre la opcion de sitio".
//
// La condicion es del DATO: `via_exposicion_persona.abre_sitio_inyeccion` la declara en
// `002_catalogos.sql`. Deberia leerse de ahi, pero `ValorDeCatalogoSalida` solo devuelve
// codigo, nombre, orden y activo — la limitacion esta anotada en el propio modelo del
// backend (`ModeloViaExposicionPersona`). Mientras el flag no viaje, la via se reconoce
// por su CODIGO, que es la PK y no cambia.
//
// Antes se comparaba contra la ETIQUETA con tildes ('Exposicion por inyeccion' escrita
// con acentos) y la semilla la escribe sin ellas: el bloque de sitio de inyeccion no
// aparecia NUNCA, con lo que `afectacion_persona.sitio_inyeccion` jamas se llenaba.
const VIA_OTROS = 'otros';
const SITIO_OTRO = 'otro';
const VIA_QUE_ABRE_SITIO = 'inyeccion';

/**
 * Marcar o desmarcar una vía de exposición de la persona. Al soltar la vía que abre el
 * sitio, el sitio elegido deja de aplicar.
 */
function conViaAlternada(p: CasoDelFormulario, via: string): CasoDelFormulario {
  const previas = p.personas?.vias ?? [];
  const vias = previas.includes(via) ? previas.filter((x) => x !== via) : [...previas, via];
  const sitioInyeccion = vias.includes(VIA_QUE_ABRE_SITIO) ? p.personas?.sitioInyeccion : '';
  return { ...p, personas: { ...p.personas, vias, sitioInyeccion } };
}

// El criterio que hace que el caso llegue senalado al Comite.
//
// Se reconoce por su CODIGO, igual que la via que abre el sitio de inyeccion y por el
// mismo motivo: la condicion es del DATO, pero el catalogo no la trae. Si manana el
// cliente agrega 'moderado' y quiere que tambien avise, no basta con darlo de alta —
// hay que decidirlo aqui. La solucion definitiva es una columna del catalogo
// (`avisa_al_comite`, como `abre_sitio_inyeccion`); queda anotado, no hecho.
const CRITERIO_QUE_AVISA_AL_COMITE = 'grave';

// La etiqueta de un codigo de catalogo que no trae nombre propio.
//
// `CATALOGOS.sexo` y `sexoPersona` son listas de CODIGOS —'macho', 'desconocido'— y no
// objetos {id, label} como el resto, porque la escala la cierra el Formato Verde y no
// hay tabla detras. Se pintaban tal cual, en minuscula, entre etiquetas capitalizadas.
// Solo cambia lo que se VE: `value` sigue siendo el codigo, que es lo que la base espera.
const enMayuscula = (codigo: unknown): string => String(codigo).charAt(0).toUpperCase() + String(codigo).slice(1);

// La etiqueta de un codigo en una lista {id, label}. Para pintar lo GUARDADO
// (que es el codigo, lo que la FK espera) con el nombre que el catalogo declara.
const etiquetaDe = (lista: OpcionDeFormulario[], id: string): string => lista.find((o) => o.id === id)?.label ?? id;

// "180 / 500 caracteres" bajo los campos con tope, para que el limite se vea ANTES de
// chocar contra el.
//
// Un `maxLength` sin contador es de los detalles que mas desconciertan: el usuario
// escribe, el campo deja de aceptar teclas y nada explica por que. Devuelve `undefined`
// cuando no hay tope configurado, que es lo que `Field` espera para no pintar nada.
//
// La UNIDAD importa: los campos numericos limitan DIGITOS, no caracteres, y decir
// "2 / 2 caracteres" en una edad confunde mas de lo que ayuda.
const cuentaAtras = (valor: unknown, tope?: number, unidad = 'caracteres'): string | undefined =>
  (tope ? `${String(valor ?? '').length} / ${tope} ${unidad}` : undefined);

// El contador del PESO, que es el unico campo cuyo tope cuenta digitos sobre un valor
// que lleva un caracter que no lo es (el punto decimal). Contarlo con `cuentaAtras` a
// secas diria "5 / 6 digitos" para "420.5", que tiene cuatro: el rotulo contradiria al
// filtro (`pesoDecimal`, que no gasta presupuesto en el punto) justo en el momento en
// que el usuario mira el contador para entender por que dejo de aceptarle teclas.
const cuentaDigitos = (valor: unknown, tope?: number): string | undefined =>
  cuentaAtras(String(valor ?? '').replaceAll(/\D/g, ''), tope, 'dígitos');

// `atencion_medica` es boolean en base y en el API. Se guarda el valor, no la etiqueta.
const OPCIONES_SI_NO = [['si', 'Sí'], ['no', 'No']];

// RN-82: «hoy» es el de la zona del país del caso. `toISOString().slice(0, 10)` era el día UTC y,
// desde las 19:00 de Lima, el formulario nacía con la recepción fechada mañana.
const vacio = (pais: string, zona: string | null): CasoDelFormulario => ({
  id: null, pais, estado: 'borrador', grave: null,
  fechaRecepcion: fechaDeHoyEnZona(zona), fechaRegistro: null,
  categorias: [],
  // Un caso NUEVO no tiene nada guardado en el servidor: lo que haya esta en pantalla.
  // Al retomar, `desdeCaso` lo pone a partir de `tiene_dato_de_personas` (T-48b).
  tieneDatoDePersonasEnServidor: false,
  // Criterio PRELIMINAR de quien registra (es veterinario). No es la clasificacion
  // formal: esa es `grave`, y la decide el Comite (Reunion 01).
  graveSugerido: '',
  // `institucion`, `esElNotificante`, `estadoSalud` y `desenlace` NO estaban en el
  // prototipo y el backend si los acepta. Se anaden aqui: un campo que la API guarda
  // y el formulario no pide es un dato que el Comite echara en falta y que nadie
  // puede rellenar despues, porque el Formato Verde ya se emitio sin el.
  notificante: { nombre: '', direccion: '', telefono: '', email: '', rol: '', institucion: '' },
  cliente: { esElNotificante: false, razonSocial: '', nombre: '', direccion: '', telefono: '', email: '', rol: '' },
  // LISTA desde el 26-ago-2026: un caso puede tener varios animales. Nace vacia, igual
  // que `medicamentos`: una fila prellenada invita a enviar un animal que nadie describio.
  animales: [],
  medicamentos: [],
  evento: {
    inicio: '', tiempoAparicion: '', unidadAparicion: 'horas', duracion: '', unidadDuracion: 'dias',
    severidad: '', desenlace: '', tratados: '', conSignos: '', muertos: '', detalle: '',
  },
  personas: null,
  evidencias: [], declaracion: '',
  evaluacion: null, informe: null, notificacion: null, seguimiento: null, historial: [],
});

// `via`, `frecuencia`, `fechaInicio` y `fechaFin` son nuevos respecto del prototipo.
//
// El prototipo tenia un solo campo de texto "Dosis, frecuencia y via de
// administracion". Se lee bien en papel y es inservible en base: nadie puede filtrar
// por via ni comparar frecuencias si las tres cosas viven en una frase. El backend
// ya las tiene separadas, asi que la pantalla las separa tambien.
//
// Las dos fechas alimentan la secuencia temporal de la causalidad (F-03). Sin ellas,
// el Comite del Sprint 2 no puede establecer cronologia y hay que volver a preguntar
// al notificante semanas despues.
// Una fila de animal en blanco. Los mismos campos que tenía el objeto único.
// Identificador local y estable de cada fila repetible. NO viaja al servidor: el
// mapeo de `lib/mapeo.js` construye el cuerpo campo a campo y nunca copia el objeto.
//
// Existe porque usar el indice como `key` de React es un fallo real aqui, no de estilo:
// las secciones de animales, medicamentos y evidencias se pueden BORRAR por el medio, y
// con claves por indice React reutiliza el nodo de la fila siguiente - el usuario ve el
// valor que escribio en la fila 3 aparecer en la 2.
//
// Contador propio y no `crypto.randomUUID()`, que no esta disponible en todos los
// entornos de prueba. Solo hace falta unicidad dentro de la lista.
let _secuencia = 0;

// EL TOTAL DE TRATADOS DE LA SECCION 6 SE CALCULA (pedido del operador, 3-sep-2026).
//
// Es la suma de "N.º tratados" de los bloques de la seccion 4. Solo cuentan los
// bloques que lo informaron; si ninguno lo hizo, el total queda VACIO y se guarda
// nulo: no se inventa un cero. Solo tratados —con signos y muertos de la seccion 6
// siguen siendo manuales, decision del operador— y se guarda en el mismo sitio de
// siempre, `evento_adverso.cantidad_tratados`, asi que RN-03 y el backend no cambian.
// Devuelve texto porque es lo que el campo guarda y lo que `entero()` convierte.
type ConteoDelAnimal = 'tratados' | 'conSignos' | 'muertos';
const sumarConteoDeLosAnimales = (animales: AnimalDelFormulario[], campo: ConteoDelAnimal): string => {
  const informados = animales.filter((a) => a[campo] !== '' && a[campo] != null);
  if (!informados.length) return '';
  return String(informados.reduce((total, a) => total + (Number(a[campo]) || 0), 0));
};
const sumarTratadosDeLosAnimales = (animales: AnimalDelFormulario[]): string =>
  sumarConteoDeLosAnimales(animales, 'tratados');
const nuevoIdDeFila = () => `f${++_secuencia}`;

const animalVacio = () => ({
  id: nuevoIdDeFila(),
  especie: '', raza: '', sexo: '', estadoFisiologico: '', edad: '', peso: '', motivo: '', estadoSalud: '',
  // Conteos de ESTE grupo (HU-03, 2-sep-2026). Los totales del evento siguen en la seccion 6.
  tratados: '', conSignos: '', muertos: '',
});

const medVacio = () => ({
  id: nuevoIdDeFila(),
  sku: '', nombre: '', forma: '', concentracion: '', registro: '', lote: '', vencimiento: '',
  dosis: '', via: '', frecuencia: '', duracion: '', fechaInicio: '', fechaFin: '',
  almacenamiento: '', administro: '', esCausa: '', masMedicamentos: '',
});

/**
 * El encabezado de la pantalla. Lo comparte `RegistroConectado` mientras trae un borrador
 * tras recargar (BUG-370): la estructura no desaparece mientras carga, y un solo texto
 * no puede divergir del otro.
 */
export const ENCABEZADO_DEL_REGISTRO = {
  eyebrow: 'Casos · Registro del caso',
  title: 'Formulario de notificación de efecto adverso',
  subtitle: 'El envío definitivo genera el expediente y avisa al Comité, a Gerencia General y a los VP. Un borrador no.',
};

// `borrador` es opcional: si llega, el formulario se abre con ese caso cargado para seguir
// editandolo (HU-01, escenario "Retomar un borrador"). Si no llega, se abre en blanco.
export default function RegistroCaso({
  usuario,
  // El numero de notificacion previsto, YA COMPUESTO por el servidor (`PE-074-2026`).
  // Llega hecho a proposito: componerlo aqui obligaria a reimplementar el relleno a
  // tres digitos y el reinicio anual de RN-07, y dos implementaciones del mismo
  // formato acaban divergiendo. `null` mientras se consulta o si la consulta falla.
  numeroPrevisto,
  // Se avisa hacia arriba al cambiar de pais: la serie es POR PAIS, asi que el numero
  // previsto deja de ser valido en cuanto el registrador cambia el selector. La
  // pantalla sigue sin saber que existe una API — solo dice que el pais cambio.
  onPaisCambiado,
  borrador,
  // HU-07 · la cadena real de subida. La cablea `RegistroConectado`, que es quien tiene
  // el `casoId` del servidor; esta pantalla sigue sin saber que existe una API (ADR-011).
  //
  // Son OPCIONALES a proposito: sin ellas la pantalla funciona como prototipo, con el
  // adjunto simulado y marcado como tal. Con ellas, el archivo se sube de verdad.
  adjuntos,
  onAdjuntar,
  // #377 · MG-ILE-377: varios archivos en una sola petición de lote. Opcional: sin ella, la
  // selección múltiple sube solo el primero, como antes.
  onAdjuntarVarios,
  cupoExcedidoDeAdjunto = null,
  presupuestoDeAdjuntos = null,
  subiendoAdjunto,
  rechazosDeAdjunto,
  falloDeAdjunto,
  onLimpiarRechazos,
  onQuitarAdjunto,
  // La huella del adjunto que se esta borrando ahora mismo, o `null`. Apaga su tacho.
  quitandoAdjunto = null,
  limitesDeAdjuntos,
  onGuardar,
  // HU-36 · avisa hacia arriba de si hay ediciones que TODAVIA no se han entregado al
  // conector. Tiene que nacer aqui: el retardo del autoguardado vive en esta pantalla y
  // desde fuera esa ventana es invisible.
  onCambiosPendientes,
  onEnviar,
  onSalir,
  valoresRetirados = [],
}: {
  usuario: UsuarioSimulado;
  numeroPrevisto?: string | null;
  onPaisCambiado?: (pais: string) => void;
  /** El caso con el que se abre, al retomar un borrador. */
  borrador?: unknown;
  adjuntos?: Adjunto[];
  onAdjuntar?: (archivo: File, opciones?: { contieneDatoDePersonas?: boolean; caso?: CasoDelFormulario }) => Promise<unknown>;
  onAdjuntarVarios?: (archivos: File[], opciones?: { contieneDatoDePersonas?: boolean; caso?: CasoDelFormulario }) => Promise<unknown>;
  /** #377-C2: el 422 del lote, con el texto del servidor. */
  cupoExcedidoDeAdjunto?: string | null;
  /** #377-C1: «6 MB de 100 MB», tal como lo dice la respuesta del lote. */
  presupuestoDeAdjuntos?: string | null;
  subiendoAdjunto?: string | null;
  rechazosDeAdjunto?: string[];
  /** Lo que fallo al subir, tal como se lanzo. Se normaliza al pintarlo. */
  falloDeAdjunto?: unknown;
  onLimpiarRechazos?: () => void;
  onQuitarAdjunto?: (huella: string) => void;
  quitandoAdjunto?: string | null;
  limitesDeAdjuntos?: ConfiguracionDeAdjuntos | null;
  onGuardar?: (caso: CasoDelFormulario) => void;
  onCambiosPendientes?: (hay: boolean) => void;
  /** Puede ser asincrono: el envio se espera para bloquear el boton mientras viaja. */
  onEnviar?: (caso: CasoDelFormulario) => void | Promise<void>;
  onSalir?: (destino?: string) => void;
  /**
   * HU-30-C4 (MG-ILE-58, tarea 30-4): los valores de catálogo que el borrador eligió y el
   * administrador retiró después (`GET /casos/{id}` → `valores_retirados`). Se muestran como
   * «Ovinos (retirado)» con el aviso del servidor mientras el campo conserve ese valor.
   */
  valoresRetirados?: ValorRetirado[];
}) {
  // Los catalogos ya no son constantes del modulo: los sirve el backend, porque
  // HU-02 y HU-30 exigen que el administrador pueda ampliarlos sin redesplegar.
  // Se desestructuran con los MISMOS nombres que tenian como import para que el
  // resto de la pantalla —la que se valido con el cliente— no cambie ni una linea.
  const { CATALOGOS, PAISES, LIMITES, CORREO, faltanPaises } = useCatalogos();
  // El declarante llega con el nombre de quien esta registrando: es obligatorio, esta al final
  // del formulario y era el error de envio mas tonto. Se puede cambiar si firma otra persona.
  // EL ESTADO DEL CASO, TIPADO. De aqui cuelga casi todo el archivo: con el tipo puesto,
  // cada `p.animales.map(...)`, cada `p.evento.tratados` y cada seccion se infieren
  // solos, y un campo mal escrito deja de compilar en vez de llegar vacio al servidor.
  const zonaDe = useZonaDelPais();
  const [c, setC] = useState<CasoDelFormulario>(
    () => (borrador as CasoDelFormulario) || { ...vacio(usuario.pais || 'PE', zonaDe(usuario.pais || 'PE')), declaracion: usuario.nombre },
  );
  // HU-29 (MG-ILE-57, tarea 29-6): el buscador de producto ofrece el maestro DEL PAIS DEL CASO y se
  // vuelve a pedir al cambiarlo. Antes salia de `useCatalogos()`, entero y sin pais.
  const maestro = useMaestroDelPais(c.pais);
  // HU-30-C4: el valor retirado de un campo, si el borrador aún lo conserva.
  const retirado = (campo: string, valorActual: unknown) => retiradoDe(valoresRetirados, campo, valorActual);
  const avisoRetirado = (r: ValorRetirado | undefined, testid: string) => (r
    ? <span data-testid={testid} className="text-amber-800">{r.aviso}</span>
    : undefined);
  const [guardado, setGuardado] = useState<Date | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  // "El dueño es el mismo que notifica": ahorra cinco campos sin quitar ninguno.
  //
  // NACE DEL BORRADOR y no en falso (reportado 2-sep-2026). Al retomar un caso
  // guardado con la casilla marcada, la casilla salia desmarcada y los campos
  // habilitados y vacios —el servidor no los guarda cuando la bandera esta en
  // verdadero—, pero la bandera del formulario seguia en verdadero. El registrador
  // escribia un nombre o elegia una profesion, y el mapeo al servidor, fiel a la
  // bandera, los descartaba en silencio. Casilla y bandera tienen que decir lo mismo.
  const [mismoQueNotificante, setMismoQueNotificante] = useState(
    () => Boolean((borrador as CasoDelFormulario | undefined)?.cliente?.esElNotificante),
  );
  // Varias secciones abiertas a la vez. Con acordeon exclusivo hacian falta 8 ciclos de abrir y
  // cerrar para llenar el formulario, nunca se veian dos secciones relacionadas juntas
  // (medicamento y evento) y no se podia repasar el conjunto antes de enviar. El acordeon
  // exclusivo sirve para contenido entre el que se ELIGE, no para uno que hay que completar entero.
  const [abierta, setAbierta] = useState(() => new Set(['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8']));
  const alternar = (k: string) => setAbierta((prev) => {
    const s = new Set(prev);
    if (s.has(k)) s.delete(k); else s.add(k);
    return s;
  });
  /** Un nodo por seccion, para poder desplazar la vista hasta ella. */
  const refs = useRef<Record<string, HTMLElement | null>>({});
  // El resumen de faltantes, para llevarle el foco al fallar el envio.
  const resumen = useRef<HTMLDivElement>(null);

  const afectaPersonas = c.categorias.includes('seguridad_en_personas');

  // Con la casilla marcada, el dueño sigue al notificante mientras se teclea.
  useEffect(() => {
    if (!mismoQueNotificante) return;
    setC((p) => ({ ...p, cliente: { ...p.cliente, nombre: p.notificante.nombre,
      direccion: p.notificante.direccion, telefono: p.notificante.telefono,
      email: p.notificante.email, rol: p.notificante.rol } }));
  }, [mismoQueNotificante, c.notificante]);

  // Numero correlativo POR PAIS, reiniciado por año (RN-07). LO DA EL SERVIDOR.
  //
  // Aqui se calculaba: el mayor de `casos` + 1. Ese calculo era correcto de puertas
  // adentro —el mayor, no la cantidad, porque la serie tiene huecos y contar produce un
  // numero ya usado— pero partia de una lista que el registro conectado no llena. Con
  // `casos` vacio el maximo era 0 y el campo decia `PE-001-2026` con la serie de Peru en
  // 73: un numero inventado, en el unico campo de la pantalla que el usuario no puede
  // corregir. La cuenta ahora la hace `serie_correlativo`, que es donde vive RN-07.
  //
  // ⚠ Es una PREVISION. Entre verla y pulsar enviar, otro registrador del mismo pais
  // puede llevarse el numero. Reservarlo al abrir el formulario seria peor: RN-07 prohibe
  // reutilizar correlativos, asi que cada borrador abandonado —y se abandonan— dejaria un
  // hueco permanente en la serie del pais ante su autoridad sanitaria.
  const numeroCaso = numeroPrevisto;

  // El pais viaja hacia arriba para que quien sepa de la API vuelva a consultar la serie.
  useEffect(() => {
    onPaisCambiado?.(c.pais);
  }, [c.pais, onPaisCambiado]);

  // Al marcar/desmarcar "seguridad en personas" se abre o se purga la seccion L2 (HU-06).
  useEffect(() => {
    if (afectaPersonas && !c.personas) {
      setC((p) => ({ ...p, personas: { consentimiento: false, iniciales: '', vias: [], sitioInyeccion: '', viaOtrosDetalle: '', sitioOtroDetalle: '', dosisRecibida: '', sexo: '', edad: '', ocupacion: '', descripcion: '', atencionMedica: '', desenlace: '' } }));
    }
  }, [afectaPersonas]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * HU-36 · CAMBIOS SIN ENTREGAR (CP-36-04, 4-sep-2026).
   *
   * Entre que el registrador teclea y que el autoguardado entrega, hay una ventana en la
   * que lo escrito solo existe en esta pantalla. Salir ahi lo pierde, y la historia pide
   * avisar antes: "salir con cambios pendientes avisa antes de descartar".
   *
   * Se mide AQUI y no en el conector porque el retardo es de esta pantalla: desde fuera,
   * el estado del guardado pasa de `limpio` a `guardando` sin pisar `pendiente`, asi que
   * mirarlo desde alli no veria nunca esta ventana.
   *
   * El caso CON EL QUE SE MONTA no cuenta: montar la pantalla —o rehidratarla al retomar—
   * no es una edicion del usuario, y contarlo avisaria de cambios que nadie hizo (romperia
   * CP-36-06, "salir sin cambios pendientes no muestra advertencia").
   *
   * Se compara por IDENTIDAD con ese caso, no con una bandera de «primer render» de un solo
   * uso: React puede correr el efecto dos veces con el mismo `c` (StrictMode lo hace al
   * montar en desarrollo), y con la bandera la segunda pasada contaba como edicion y el
   * menu avisaba sin que nadie escribiera (MG-ILE-376, CP-36-06). Cualquier edicion crea
   * un objeto nuevo (`setC` nunca muta), asi que solo el montaje coincide.
   */
  const [sinEntregar, setSinEntregar] = useState(false);
  const casoAlMontar = useRef(c);
  useEffect(() => {
    onCambiosPendientes?.(sinEntregar);
  }, [sinEntregar, onCambiosPendientes]);

  // Autoguardado (HU-36): cada cambio, con retardo, y aviso visible.
  useEffect(() => {
    if (c === casoAlMontar.current) return undefined;
    // Sin notificante ni categoria el caso no se abre todavia, asi que lo escrito NO
    // llega a ningun sitio: eso es justamente "sin entregar", y se queda marcado.
    setSinEntregar(true);
    if (!c.notificante.nombre && !c.categorias.length) return undefined;
    const t = setTimeout(() => {
      // Ya NO se estampa el numero previsto en `c.id`. Se hacia cuando el numero se
      // calculaba aqui y era lo unico que identificaba al caso; ahora es una prevision
      // del servidor que puede quedar obsoleta, y congelarla dentro del borrador la
      // convertiria en un dato guardado que nadie volveria a comprobar. La identidad
      // del caso es el uuid que devuelve `POST /casos`, y de eso se encarga quien
      // habla con la API.
      onGuardar?.(c);
      setGuardado(new Date());
      setSinEntregar(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [c]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escribe por RUTA (`notificante.correo`, `animales.0.especie`). El recorrido es
  // dinamico, asi que aqui se trabaja sobre una vista sin tipo del objeto y se devuelve
  // el caso: es el unico punto del archivo donde el acceso no se puede describir, y
  // acotarlo a estas cuatro lineas es mejor que repartir `any` por cada campo.
  const set = (path: string, val: unknown) => {
    setC((p) => {
      const n = structuredClone(p);
      const ks = path.split('.');
      let o = n as Record<string, unknown>;
      for (let i = 0; i < ks.length - 1; i++) o = o[ks[i]] as Record<string, unknown>;
      o[ks[ks.length - 1]] = val;
      return n;
    });
  };

  // CP-03-13/14 y CP-06-12/13/14. Conmutar la categoria de personas DESTRUYE datos
  // en los dos sentidos, y hasta ahora lo hacia sin preguntar:
  //
  //   · al MARCARLA se limpian raza y estado fisiologico (nota E26 del Formato Verde);
  //   · al DESMARCARLA se purga el bloque L2 entero por minimizacion (RNF-02).
  //
  // Lo segundo es lo grave: es dato de salud de una persona, costo pedirlo, y se
  // perdia con un clic accidental sin forma de recuperarlo. HU-06 ya lo decia —"el
  // sistema advierte y, confirmado, deja de exigir esos datos"—, solo que la
  // advertencia no existia.
  //
  // Solo se pregunta si hay ALGO que perder: confirmar por confirmar entrena a la
  // gente a aceptar sin leer, y entonces la advertencia deja de proteger.
  /** La confirmacion pendiente al conmutar una categoria que destruiria datos (RNF-02). */
  const [confirmarCategoria, setConfirmarCategoria] = useState<
    { id: string; yaEstaba: boolean; enRiesgo: unknown } | null
  >(null);

  const pedirCategoria = (id: string) => {
    const yaEstaba = c.categorias.includes(id);
    const enRiesgo = datosEnRiesgoAlConmutar(c, id, yaEstaba);
    if (enRiesgo) {
      setConfirmarCategoria({ id, yaEstaba, enRiesgo });
      return;
    }
    toggleCategoria(id);
  };

  const toggleCategoria = (id: string) => {
    setC((p) => {
      const has = p.categorias.includes(id);
      const cats = has ? p.categorias.filter((x: string) => x !== id) : [...p.categorias, id];
      // Al desmarcar personas se purga el dato L2, por minimizacion (RNF-02). Es lo UNICO
      // que sigue destruyendose al conmutar la categoria.
      //
      // Lo que ya NO ocurre (26-ago-2026): marcarla fijaba la especie en 'Humano' y
      // limpiaba raza, estado fisiologico y sexo, porque la seccion 4 pasaba a describir a
      // la persona. Desde que las dos coexisten, la seccion 4 es siempre la del animal y
      // sus datos no tienen por que perderse: un caso donde el medicamento mato a la vaca
      // Y pincho al veterinario necesita la raza de la vaca igual que antes.
      const personas = id === 'seguridad_en_personas' && has ? null : p.personas;
      // Desmarcar ORDENA la purga en el servidor (T-48b), asi que el aviso de que alla
      // habia dato deja de ser cierto en cuanto se confirma. Sin apagarlo, volver a
      // marcar y desmarcar pediria confirmacion por un dato que ya no existe — y una
      // advertencia que salta sobre nada entrena a aceptarla sin leer, que es
      // exactamente lo que HU-06 no quiere.
      const enServidor =
        id === 'seguridad_en_personas' && has ? false : p.tieneDatoDePersonasEnServidor;
      return { ...p, categorias: cats, personas, tieneDatoDePersonasEnServidor: enServidor };
    });
  };

  // El total de tratados de la seccion 6 sigue a los animales (ver `sumarTratadosDeLosAnimales`).
  // En un efecto y no derivado al pintar, para que el valor viva en el estado como
  // siempre: el autoguardado, el mapeo al servidor y la validacion de coherencia lo
  // leen de `c.evento.tratados` sin enterarse de que ahora se calcula. Corre tambien al
  // montar: un borrador antiguo con el total escrito a mano pasa a decir lo que digan
  // sus animales, que es lo que el operador decidio.
  const sumaDeTratados = sumarTratadosDeLosAnimales(c.animales);
  useEffect(() => {
    if (c.evento.tratados === sumaDeTratados) return;
    set('evento.tratados', sumaDeTratados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sumaDeTratados]);

  /**
   * CON SIGNOS Y MUERTOS TAMBIEN SIGUEN A LOS ANIMALES, PERO SE PUEDEN CORREGIR
   * (pedido del operador, 7-sep-2026).
   *
   * Hasta hoy eran manuales del todo, y quien rellenaba los bloques de la seccion 4
   * tenia que volver a sumar a mano lo que el formulario ya sabia.
   *
   * ⚠ LA DIFERENCIA CON `tratados`, y es deliberada: aquel se RECALCULA siempre y es de
   * solo lectura; estos dos SE PUEDEN ESCRIBIR, y lo escrito manda. Por eso no vale el
   * mismo efecto: si volviera a sumar tras cada cambio, editar no serviria de nada
   * —tocar un animal borraria lo tecleado— y el campo seria editable solo en apariencia.
   *
   * COMO SE DECIDE:
   *   · solo se sincroniza cuando la SUMA CAMBIA, no en cada render. Al montar un
   *     borrador guardado no se toca nada: lo guardado es lo que la persona vio por
   *     ultima vez, y pisarlo al abrir seria perder su trabajo sin avisar.
   *   · en cuanto alguien escribe en el campo, deja de seguir para el resto de la
   *     sesion. `tocados` no viaja al servidor ni al espejo local: es de esta edicion.
   */
  const sumaDeConSignos = sumarConteoDeLosAnimales(c.animales, 'conSignos');
  const sumaDeMuertos = sumarConteoDeLosAnimales(c.animales, 'muertos');
  const totalesTocados = useRef({ conSignos: false, muertos: false });
  const sumasPrevias = useRef({ conSignos: sumaDeConSignos, muertos: sumaDeMuertos });
  useEffect(() => {
    // `as const` para que el indice sea la union de las dos claves y no `string`: sin
    // el, indexar los dos `useRef` de arriba no compila en modo estricto.
    for (const campo of ['conSignos', 'muertos'] as const) {
      const suma = campo === 'conSignos' ? sumaDeConSignos : sumaDeMuertos;
      if (sumasPrevias.current[campo] === suma) continue;   // la suma no se movio
      sumasPrevias.current[campo] = suma;
      if (totalesTocados.current[campo]) continue;          // lo escrito manda
      set(`evento.${campo}`, suma);
    }
  }, [sumaDeConSignos, sumaDeMuertos]);

  // EL FOCO ENTRA EN LA FILA RECIEN AGREGADA (reportado por pruebas de accesibilidad,
  // 2-sep-2026). Los botones "Agregar animal" y "Agregar medicamento" van DEBAJO de
  // la lista, asi que la fila nueva nace por encima del boton en el DOM: el foco se
  // quedaba en el boton y el siguiente Tab saltaba a la cabecera de la seccion
  // siguiente. Para entrar en la fila habia que retroceder con Shift+Tab.
  //
  // Se lleva el foco al primer campo de la fila —que es ademas su obligatorio— con la
  // misma marca `data-campo` que usa el resumen de faltantes (`irACampo`). En el
  // siguiente tick, porque la fila todavia no existe cuando se ejecuta el handler.
  // No toca el estado ni la validacion: solo a donde mira el teclado.
  const enfocarCampoNuevo = (campo: string) => {
    setTimeout(() => {
      // ⚠ SIN `preventScroll`, y a proposito. El 7-sep se probo ponerlo creyendo que
      // este `focus()` era el que desviaba la pagina; no lo era —el culpable era el
      // plegado en el `mousedown`, ver el manejador del foco— y ademas resulto danino:
      // con una lista larga, la fila nueva puede nacer fuera del area visible y el foco
      // quedaria en un control que no se ve (WCAG 2.4.11, Focus Not Obscured).
      // El desplazamiento implicito de `focus()` es aqui lo correcto: trae a la vista la
      // fila que el usuario acaba de pedir.
      document.querySelector<HTMLElement>(`[data-campo="${campo}"]`)?.focus();
    }, 0);
  };

  // Mismo criterio que `addMed`: la fila existe porque el usuario la pidió, así que su
  // obligatorio —la especie— entra en juego con ella y avisa de inmediato.
  const addAnimal = () => {
    const i = c.animales.length;
    tocar(`animales.${i}.especie`);
    setC((p) => ({ ...p, animales: [...p.animales, animalVacio()] }));
    enfocarCampoNuevo(`animales.${i}.especie`);
  };
  const delAnimal = (i: number) => setC((p) => ({ ...p, animales: p.animales.filter((_, x) => x !== i) }));
  const setAnimal = (i: number, k: string, v: string) => setC((p) => ({
    ...p, animales: p.animales.map((a, x) => (x === i ? { ...a, [k]: v } : a)),
  }));

  // Al agregar un medicamento, sus dos campos obligatorios nacen YA MARCADOS como
  // tocados, así que el aviso rojo aparece de inmediato en vez de esperar al envío.
  //
  // Es una excepción deliberada a la regla general del formulario —"no se acusa a nadie
  // de un error que no ha cometido"—, y se sostiene porque aquí el usuario SÍ hizo algo:
  // pulsó "Agregar medicamento". La fila existe porque él la pidió, y sus obligatorios
  // entran en juego con ella. Sin esto, una fila recién creada se ve idéntica a una
  // completa hasta que se pulsa enviar, y la respuesta de causalidad —que el servidor
  // exige, trigger `tg_caso_exige_causalidad`— es la que más se olvida.
  const addMed = () => {
    const i = c.medicamentos.length;
    tocar(`medicamentos.${i}.sku`);
    tocar(`medicamentos.${i}.esCausa`);
    setC((p) => ({ ...p, medicamentos: [...p.medicamentos, medVacio()] }));
    enfocarCampoNuevo(`medicamentos.${i}.sku`);
  };
  const delMed = (i: number) => setC((p) => ({ ...p, medicamentos: p.medicamentos.filter((_, x) => x !== i) }));

  // RESPONDER «Si» A «¿se administro mas medicamentos simultaneamente?» ABRE EL BLOQUE
  // DEL SIGUIENTE (CP-04-04, 3-sep-2026).
  //
  // Es lo que HU-04 pide en dos sitios —la tabla de datos ("si es Si, se repite la
  // seccion") y el «Listo cuando» ("abre una seccion repetible independiente")—, y lo
  // que el equipo de pruebas fue a buscar. Hasta ahora la respuesta solo se guardaba y
  // el bloque habia que pedirlo aparte, con el boton; la unica pista era el texto de
  // ayuda. Quien responde «Si» esta declarando que hay otro medicamento: se le abre el
  // sitio donde escribirlo.
  //
  // Dos guardas:
  //   1. SOLO SI ES EL ULTIMO bloque. Corregir la respuesta del primero cuando el
  //      segundo ya existe no debe crear un tercero vacio.
  //   2. Pasar de «Si» a «No» NO borra nada. Quitar un bloque es una accion explicita,
  //      con su boton (escenario "Quitar un medicamento agregado por error"), y borrar
  //      por un cambio de opinion se llevaria por delante lo ya escrito.
  const responderMasMedicamentos = (i: number, valor: string) => {
    setMed(i, 'masMedicamentos', valor);
    if (valor !== 'Sí' || i !== c.medicamentos.length - 1) return;
    addMed();
  };
  const setMed = (i: number, k: string, v: string) => setC((p) => ({ ...p, medicamentos: p.medicamentos.map((m, x) => (x === i ? { ...m, [k]: v } : m)) }));
  const elegirSku = (i: number, sku: string) => {
    const p = maestro.productos.find((m) => m.sku === sku);
    if (!p) return;
    setC((prev) => ({
      ...prev,
      medicamentos: prev.medicamentos.map((m, x) =>
        x === i ? { ...m, sku: p.sku, nombre: p.nombre, forma: p.forma, concentracion: p.concentracion, registro: p.registro } : m),
    }));
  };

  // Con `onAdjuntar` cableado, la subida es REAL. Sin el, se simula: es lo que permite
  // seguir usando esta pantalla como prototipo sin API detras (ADR-011), y va marcado
  // en pantalla para que nadie confunda un adjunto simulado con uno subido.
  const subidaReal = typeof onAdjuntar === 'function';

  /**
   * CP-07-19 · «Esta evidencia contiene datos de personas» (4-sep-2026).
   *
   * HU-07 lo pide dos veces: "MARCAR «contiene datos de personas» activa el tratamiento
   * L2 del adjunto" en los criterios, y "la clasificacion es MANUAL" en el cuadro de
   * riesgos. No existia: la marca se derivaba sola de si el caso declaraba la categoria
   * de afectacion a personas, asi que no habia nada que marcar y el caso quedo bloqueado.
   *
   * Empieza DESMARCADA en cada archivo, que es lo que comprueba el primer paso del caso,
   * y se limpia despues de adjuntar: es una decision POR ARCHIVO, y dejarla pegada haria
   * que la segunda evidencia heredara la respuesta de la primera sin que nadie lo pida.
   */
  const [evidenciaConDatoDePersonas, setEvidenciaConDatoDePersonas] = useState(false);

  // QUITAR UNA EVIDENCIA (HU-07, escenario "Eliminar un adjunto antes de enviar").
  //
  // Con subida real el borrado lo hace el SERVIDOR —deja su asiento de auditoria, que es
  // lo que la HU pide— y la lista se refresca desde su respuesta. Con subida simulada no
  // hay servidor, asi que se quita de la lista local por posicion.
  //
  // Se identifica por HUELLA y no por posicion en el caso real: la lista se reordena al
  // retomar y un indice apuntaria a otra fila.
  const quitarEvidencia = (i: number) => {
    if (!subidaReal) {
      setC((p) => ({ ...p, evidencias: p.evidencias.filter((_, x) => x !== i) }));
      return;
    }
    const huella = evidencias[i]?.huella;
    if (huella) onQuitarAdjunto?.(huella);
  };
  const selectorDeArchivo = useRef<HTMLInputElement>(null);

  const addEvidencia = () => {
    tocar('evidencias');
    if (subidaReal) {
      onLimpiarRechazos?.();
      selectorDeArchivo.current?.click();
      return;
    }
    const n = prompt('Nombre del archivo a adjuntar (simulado):', 'historia_clinica.pdf');
    if (!n) return;
    setC((p) => ({ ...p, evidencias: [...p.evidencias, { id: nuevoIdDeFila(), nombre: n, tipo: 'Historia clínica', fecha: fechaDeHoyEnZona(zonaDe(c.pais)), l2: evidenciaConDatoDePersonas || afectaPersonas }] }));
    setEvidenciaConDatoDePersonas(false);
  };

  // `contieneDatoDePersonas` decide el CONTENEDOR, no un metadato (ADR-004): si el caso
  // declara afectacion a personas, la evidencia va al contenedor con caducidad de 5
  // anios. Marcarlo mal no da error hoy; lo da en el ano 5, cuando el dato de una
  // persona resulte imposible de borrar.
  //
  // SE COMBINAN LAS DOS SENALES, no se sustituye una por la otra (CP-07-19). La casilla
  // AÑADE proteccion y nunca la quita: un caso que declara afectacion a personas sigue
  // mandando sus evidencias al contenedor L2 aunque el registrador no marque nada. La HU
  // dice que marcar ACTIVA el tratamiento L2; no dice que no marcar lo desactive, y para
  // un dato de salud la asimetria es la lectura correcta — el coste de proteger de mas es
  // guardar menos tiempo un archivo; el de proteger de menos es un incumplimiento.
  const alElegirArchivo = async (evento: ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(evento.target.files ?? []);
    // El input se limpia SIEMPRE: sin esto, elegir el mismo archivo dos veces seguidas
    // no dispara `change` la segunda vez y el usuario cree que la pantalla se colgo.
    evento.target.value = '';
    if (!elegidos.length) return;
    // Se manda TAMBIEN el caso: si el borrador todavia no existe en el servidor, quien
    // cablea esto lo abre antes de subir, y necesita el pais y la fecha de recepcion.
    // #377-C4: la casilla de personas vale para TODA la seleccion.
    const opciones = { contieneDatoDePersonas: evidenciaConDatoDePersonas || afectaPersonas, caso: c };
    // #377: con varios, UNA peticion de lote; con uno solo, el camino de siempre.
    if (elegidos.length > 1 && onAdjuntarVarios) await onAdjuntarVarios(elegidos, opciones);
    else await onAdjuntar?.(elegidos[0], opciones);
    setEvidenciaConDatoDePersonas(false);
  };

  // Las evidencias que ve el formulario. Con subida real son las del servidor; el
  // estado local sigue existiendo para el modo prototipo. Una sola lista, para que la
  // validacion de envio (C-01) cuente lo mismo en los dos casos.
  const evidencias = evidenciasVisibles(subidaReal, adjuntos, c.evidencias, zonaDe(c.pais));

  // La lista real vive en el hook, no en `c`. Se refleja en el estado del formulario
  // porque `evaluarFormulario` cuenta `c.evidencias` para el control C-01, y tener dos
  // fuentes de verdad ahi significaria que el boton de enviar y la lista en pantalla
  // pueden discrepar.
  useEffect(() => {
    if (!subidaReal) return;
    setC((p) => (p.evidencias.length === evidencias.length ? p : { ...p, evidencias }));
  }, [subidaReal, evidencias]);

  // Validacion: los obligatorios del AF §7b + coherencia de conteos.
  //
  // La REGLA (que esta mal) vive en `validacion.js`; CUANDO se ensena, en
  // `useValidacionCampos`: al salir de cada campo, y de golpe al pulsar enviar. Separarlo
  // importa porque son dos decisiones distintas —la regla no cambia, el momento de
  // mostrarla decide si el formulario ayuda o regana— y porque la regla asi se prueba sola.
  const {
    porSeccion, cuantosFaltan, completo, intentoEnvio, errorDe, tocar, tocarTodo,
  } = useValidacionCampos(c, afectaPersonas, CORREO);

  const SECCIONES = [
    { key: 's1', n: 1, label: 'Encabezado' },
    { key: 's2', n: 2, label: 'Notificante y dueño' },
    { key: 's3', n: 3, label: 'Identificación del EA' },
    // "Descripción del paciente", como en el Formato Verde (pedido del operador,
    // 3-sep-2026): el registrador transcribe del papel y la pantalla tiene que llamar a
    // la seccion como el papel. Se llamo "Animales" mientras conmutaba a "Paciente" al
    // marcar personas; desde el 26-ago-2026 ya no conmuta —el animal va aqui y la
    // persona en la 7— y el nombre corto dejo de tener motivo.
    { key: 's4', n: 4, label: 'Descripción del paciente' },
    { key: 's5', n: 5, label: 'Medicamentos' },
    { key: 's6', n: 6, label: 'Evento adverso' },
    { key: 's7', n: 7, label: 'EA en personas', l2: true, off: !afectaPersonas },
    { key: 's8', n: 8, label: 'Declaración' },
  ];

  // ScrollSpy: la entrada activa del indice sigue a la seccion que se esta leyendo.
  const { activa: seccionEnLectura, fijar: fijarSeccion } = useSeccionEnLectura(
    SECCIONES.filter((sk) => !sk.off).map((sk) => sk.key), refs);

  // Clic en el indice: la seccion se despliega si estaba plegada, pasa a ser la activa y la
  // pantalla baja hasta ella (seco si el sistema pide reducir movimiento).
  const irA = (k: string) => {
    setAbierta((prev) => new Set(prev).add(k));
    fijarSeccion(k);
    setTimeout(() => refs.current[k]?.scrollIntoView({ behavior: comportamientoDelDesplazamiento(), block: 'start' }), 60);
  };

  // COLAPSO AUTOMATICO DE LO COMPLETADO (pedido del operador, 28-ago-2026): al
  // terminar una seccion, se cierra sola para despejar el camino hacia la
  // siguiente.
  //
  // ⚠ EL DISPARO CAMBIO EL 28-ago-2026 POR LA QUEJA DEL EQUIPO: "lleno 1 campo y la
  // seccion se cierra". Se disparaba con el `focusout`, y "completa" significa
  // "sin obligatorios pendientes": en una seccion con UN solo obligatorio —el
  // nombre del notificante, la declaracion— bastaba salir de ese campo, aunque
  // fuera hacia la Direccion de al lado, para que la seccion se plegara con todos
  // sus opcionales en blanco. El registrador la percibia como "se cerro sola y me
  // escondio campos que aun iba a llenar", que es exactamente lo contrario de
  // despejar el camino.
  //
  // Ahora el colapso se dispara SOLO cuando el foco ENTRA en un campo de OTRA
  // seccion, que es la unica senal inequivoca de progresion hacia adelante: la
  // seccion se queda abierta mientras se siga trabajando dentro de ella, sin
  // importar cuantos opcionales queden. Las tres guardas siguen en pie:
  //   1. NUNCA se cierra con el foco dentro — por construccion: solo se cierra lo
  //      que NO contiene el elemento que acaba de recibir el foco.
  //   2. Reabrirla a mano se respeta: al colapsar se deja de estar pendiente, y
  //      solo se vuelve a estarlo tras una nueva transicion incompleta -> completa.
  //   3. Al momento de cerrar se re-verifica que SIGUE completa: entre quedar
  //      pendiente y entrar el foco en otra seccion pudo borrarse un dato.
  const porSeccionRef = useRef(porSeccion);
  porSeccionRef.current = porSeccion;
  const completasPrevias = useRef(new Set());
  // Las secciones que ya se pueden plegar y esperan a que el foco pase a otra.
  // En `ref` y no en estado: solo la lee el manejador del foco, y en estado
  // provocaria un render por cada seccion que se completa sin cambiar la pantalla.
  const pendientesDeColapsar = useRef(new Set<string>());
  // ¿Hay un boton del raton apretado ahora mismo? Decide si el plegado se hace ya o
  // se espera a soltarlo. Ver la nota en el manejador del foco.
  const punteroAbajo = useRef(false);
  const clavesCompletas = SECCIONES
    .filter((sk) => !sk.off && !(porSeccion[sk.key]?.length))
    .map((sk) => sk.key);
  const firmaCompletas = clavesCompletas.join('|');
  /**
   * EL MONTAJE NO ES "ACABAR DE COMPLETAR UNA SECCION" (reportado por el operador,
   * 7-sep-2026, y confirmado con dos auditorias independientes).
   *
   * EL DEFECTO. `completasPrevias` arrancaba VACIO, asi que el primer render se
   * contabilizaba como una transicion incompleta -> completa de todo lo que ya venia
   * completo. Y siempre viene algo: la seccion 1 nace con `pais` y `fechaRecepcion`
   * —sus dos unicos obligatorios— y la 8 con el declarante; al retomar un borrador,
   * cinco o seis a la vez. Resultado: nada mas abrir el caso habia secciones esperando
   * plegarse sin que el registrador hubiera hecho nada.
   *
   * ⚠ Y LO QUE LAS DISPARABA ERA EL PRIMER CLIC, en el peor momento posible. `focusin`
   * llega en el MOUSEDOWN, antes de que el clic termine: se plegaba la seccion 1 —que
   * esta ARRIBA DEL TODO—, el formulario subia ~150 px con el cursor quieto, y el
   * `mouseup` caia en otro sitio. El navegador dispara entonces el `click` sobre el
   * ancestro comun, asi que NO llegaba a su boton. De ahi "doy clic en Agregar animal y
   * se desvia; al segundo intento ya se queda": el bucle de mas abajo vacia la cola
   * incondicionalmente, asi que el segundo clic ya no encontraba nada que plegar.
   *
   * Se arregla sembrando la referencia en la primera medida: lo que ya estaba completo
   * al abrir NO es progresion del usuario y no puede quedar pendiente de plegado.
   */
  const yaSeMidio = useRef(false);
  useEffect(() => {
    const nuevas = yaSeMidio.current
      ? clavesCompletas.filter((k) => !completasPrevias.current.has(k))
      : [];
    yaSeMidio.current = true;
    completasPrevias.current = new Set(clavesCompletas);
    // Lo que volvio a tener faltantes deja de estar pendiente: si mas tarde se
    // completa otra vez, entrara por la transicion, que es lo que la guarda 2 pide.
    // Se itera el Set directamente aunque se borre dentro: borrar la clave en curso
    // durante un `for...of` sobre un Set esta definido por la especificacion y no
    // salta ni repite entradas. La copia con spread era redundante (S7747).
    for (const k of pendientesDeColapsar.current) {
      if (!completasPrevias.current.has(k)) pendientesDeColapsar.current.delete(k);
    }
    nuevas.forEach((k) => pendientesDeColapsar.current.add(k));
    // `firmaCompletas` y no el array: la identidad del array cambia en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaCompletas]);

  /**
   * LA DECLARACION SE PLIEGA AL ABRIR EL CASO, no al primer clic (7-sep-2026).
   *
   * Con la correccion de arriba ya nada se pliega solo al empezar, y eso cambiaba lo que
   * el registrador ve: la seccion 8 se quedaria abierta durante toda la captura, cuando
   * hoy se cierra en cuanto tocas algo. Se conserva ese comportamiento pero moviendolo al
   * momento en que NO puede molestar: la apertura del caso, antes de que haya un cursor
   * en ningun sitio.
   *
   * SOLO LA 8, y no la 1: la Declaracion esta al FINAL del formulario, asi que plegarla
   * no arrastra nada de lo que hay encima. El Encabezado se queda abierto —como hoy—
   * porque plegar algo que esta arriba si mueve todo lo demas, y porque ahi viven el
   * pais y la fecha, que a veces son lo primero que se corrige.
   *
   * `useLayoutEffect` y no `useEffect`: se pliega ANTES de que el navegador pinte, asi
   * que no se ve abrirse y cerrarse.
   */
  useLayoutEffect(() => {
    if (!clavesCompletas.includes('s8')) return;   // sin declarante: no hay nada completo que plegar
    setAbierta((prev) => {
      const abiertas = new Set(prev);
      abiertas.delete('s8');
      return abiertas;
    });
    // Solo al montar: despues, la 8 se pliega por la via normal como cualquier otra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `focusin` y no `focusout`: la pregunta ya no es "¿salio el foco de aqui?" sino
  // "¿a donde entro?". Se escucha en el documento —una sola vez, sin re-suscribir—
  // porque el destino puede estar en cualquier seccion y lo que interesa es la
  // relacion entre las dos, no el recorrido del evento.
  useEffect(() => {
    /**
     * Pliega las secciones que ya estaban terminadas, anclando la vista antes.
     *
     * ⚠ VIVE AQUI, AL NIVEL DEL EFECTO, Y NO DENTRO DEL MANEJADOR. Metida ahi era un
     * QUINTO nivel de anidamiento —componente > efecto > manejador > plegar >
     * actualizador de estado— y `typescript:S2004` la marcaba con razon. Se conto mal
     * al portarla: la funcion del componente cuenta como nivel, no se empieza en el
     * efecto.
     *
     * Y no pierde nada por estar fuera: lo unico que tomaba del cierre eran el elemento
     * enfocado y la lista a cerrar, que ahora recibe. Asi tampoco se recrea en cada foco.
     */
    const plegar = (enfocado: Element, aCerrar: string[]) => {
      // EL ANCLA, ANTES DE PLEGAR (5-sep-2026). Ver `useLayoutEffect` mas abajo: se
      // apunta donde esta AHORA, en pantalla, el elemento que tiene el foco.
      anclaDelColapso.current = {
        el: enfocado,
        arriba: enfocado.getBoundingClientRect?.().top ?? 0,
      };
      setAbierta((prev) => {
        const abiertas = new Set(prev);
        // `for...of` y no `forEach`: la funcion del `forEach` seria un nivel mas de
        // anidamiento y `typescript:S2004` lo marca con razon. El bucle hace lo mismo
        // sin abrir otro ambito, y de paso se lee sin saltar de linea.
        for (const k of aCerrar) abiertas.delete(k);
        return abiertas;
      });
    };

    const alEntrarElFoco = (evento: globalThis.FocusEvent) => {
      const pendientes = pendientesDeColapsar.current;
      if (!pendientes.size || !evento.target) return;
      // LA CABECERA DEL ACORDEON NO CUENTA COMO PROGRESION (reportado 31-ago-2026).
      //
      // T-44 §4 lo decidio con estas palabras: el colapso se dispara "cuando el foco
      // entra a UN CAMPO de otra seccion (progresion real hacia adelante)". Se
      // implemento con cualquier `focusin` dentro de otra seccion, y la cabecera esta
      // dentro. Efecto para el registrador: da clic en el titulo de "Animales" para
      // trabajar ahi, y de un solo gesto se le cierran DOS secciones —Animales por el
      // propio acordeon, y la anterior por este colapso—, la pagina salta hacia arriba
      // al acortarse, y termina mirando "Medicamentos". Se percibe como que el
      // formulario lo expulsa a la siguiente seccion.
      //
      // Plegar o desplegar no es avanzar: es mirar. Quien de verdad avanza pone el foco
      // en un campo, y entonces si se colapsa lo que quedo terminado.
      if ((evento.target as Element).closest?.('[data-cabecera-seccion]')) return;
      const secciones = refs.current;
      const entro = Object.keys(secciones).find((k) => secciones[k]?.contains(evento.target as Node));
      // Fuera de toda seccion —la cabecera, el resumen del pie, el menu— NO es
      // progresion: se espera a que el foco entre de verdad en otra seccion.
      if (!entro) return;
      const aCerrar: string[] = [];
      for (const k of pendientes) {
        if (k === entro) continue;                          // guarda 1
        pendientes.delete(k);
        if (porSeccionRef.current[k]?.length) continue;     // guarda 3
        aCerrar.push(k);
      }
      if (!aCerrar.length) return;
      const enfocado = evento.target as Element;

      /**
       * ⚠ PLEGAR CON EL BOTON DEL RATON TODAVIA ABAJO ROMPE EL CLIC (7-sep-2026).
       *
       * `focusin` llega en el MOUSEDOWN, no en el clic. Si se pliega aqui, el DOM se
       * mueve ENTRE el `mousedown` y el `mouseup`: el navegador dispara el `click` sobre
       * el ancestro comun de los dos, que ya no es el control que el usuario apunto. El
       * clic se pierde y hay que repetirlo — el defecto que el operador reporto.
       *
       * Con el raton se espera al `pointerup`: primero aterriza el clic, despues se
       * pliega. Con TECLADO no hay boton abajo y se pliega en el acto, como hasta ahora.
       *
       * `once`: el oyente se retira solo. Y se registra en burbuja, no en captura, para
       * que corra DESPUES de que `punteroAbajo` se ponga a false.
       */
      if (punteroAbajo.current) {
        document.addEventListener('pointerup', () => plegar(enfocado, aCerrar), { once: true });
      } else {
        plegar(enfocado, aCerrar);
      }
    };
    // Se sigue el estado del puntero en CAPTURA para que llegue antes que cualquier otro
    // oyente, incluido el `plegar` diferido de arriba.
    const alBajarPuntero = () => { punteroAbajo.current = true; };
    const alSubirPuntero = () => { punteroAbajo.current = false; };
    document.addEventListener('pointerdown', alBajarPuntero, true);
    document.addEventListener('pointerup', alSubirPuntero, true);
    document.addEventListener('focusin', alEntrarElFoco);
    return () => {
      document.removeEventListener('pointerdown', alBajarPuntero, true);
      document.removeEventListener('pointerup', alSubirPuntero, true);
      document.removeEventListener('focusin', alEntrarElFoco);
    };
  }, []);

  /**
   * QUE LA PAGINA NO SE MUEVA BAJO EL CURSOR (reportado por el operador, 5-sep-2026).
   *
   * El plegado automatico funciona como se pidio, pero tiene un efecto que nadie habia
   * mirado: la seccion que se pliega esta casi siempre POR ENCIMA de aquella a la que el
   * registrador acaba de saltar —terminas la 2, avanzas a la 3, y se pliega la 2—. Al
   * desaparecer ese alto, todo lo de abajo SUBE. El usuario no se movio: se movio el
   * formulario debajo de el, y lo vive como "hice clic y me fui a otra seccion".
   *
   * No se resuelve dejando de plegar hacia arriba: plegar lo que acabas de terminar ES la
   * funcion, y hacerlo solo hacia abajo la anularia en el recorrido normal.
   *
   * Se resuelve compensando. Se mide donde estaba en pantalla el elemento que recibio el
   * foco, y despues de que el DOM se actualice se desplaza la ventana justo lo que ese
   * elemento se movio. Resultado: la seccion se pliega igual, y lo que el usuario esta
   * mirando se queda exactamente donde estaba.
   *
   * `useLayoutEffect` y no `useEffect`: hay que corregir ANTES de que el navegador pinte,
   * o el salto se ve igual aunque luego se deshaga.
   *
   * ============================================================================
   * ⚠ SE CORRIGIO EL 9-sep-2026: ESTA COMPENSACION NUNCA LLEGO A EJECUTARSE.
   * ============================================================================
   * Se escribio contra `window.scrollBy`, y ESTA APLICACION NO DESPLAZA LA VENTANA.
   * `AppShell` monta en `h-screen overflow-hidden` y el unico contenedor desplazable
   * es su `<main className="flex-1 overflow-y-auto">`. Medido en Chromium sobre el
   * borrador real: `document.documentElement.scrollHeight` = 900 —exactamente el alto
   * de la ventana, o sea nada que desplazar— mientras `main.scrollHeight` = 3736.
   * `window.scrollBy` era un no-op silencioso: el calculo salia bien y se tiraba.
   *
   * Es el mismo error que ya se habia encontrado y borrado en el hook `alturaEstable`
   * (ver la cabecera de `tests/elFormularioNoEmpuja.test.jsx`); esta copia sobrevivio.
   *
   * LO QUE COSTABA, medido a distintas alturas de scroll con el plegado de la seccion 2
   * (≈596 px) y muestreo fotograma a fotograma del campo pulsado:
   *
   *   scrollTop=800   pulsando `categorias`      -> el campo salta de y=500 a y=-97
   *   scrollTop=1000  pulsando `categorias`      -> el campo salta de y=300 a y=-297
   *   scrollTop=1400  pulsando `evento.inicio`   -> 0.5 px
   *
   * O sea ~597 px de salto —el campo se va FUERA DE LA PANTALLA— y `scrollTop` sin
   * cambiar: no compensaba nadie. Los casos de 0.5 px son los que salvaba el scroll
   * anchoring de Chrome por su cuenta, que elige su nodo ancla como quiere: cuando ese
   * nodo cae dentro de la seccion que se pliega, se rinde y no compensa. De ahi el
   * "a veces si, a veces no" que el operador reportaba.
   *
   * ⚠ NO se arregla poniendo `overflow-anchor: none` en el `<main>`. Se probo: apagarlo
   * convierte los casos que hoy salen bien (0.5 px) en saltos de -656 px, porque deja al
   * descubierto todo lo que el navegador si estaba compensando. La red del navegador se
   * conserva; esto solo cubre los huecos que deja.
   *
   * En jsdom `getBoundingClientRect` devuelve ceros, asi que `desplazado` da 0 y no se
   * toca nada: las pruebas del plegado siguen valiendo tal cual. La compensacion en si
   * solo se puede comprobar en un navegador de verdad.
   */
  /** Donde estaba en pantalla el elemento enfocado, para no perder el sitio al plegar. */
  const anclaDelColapso = useRef<{ el: Element; arriba: number } | null>(null);
  useLayoutEffect(() => {
    const ancla = anclaDelColapso.current;
    anclaDelColapso.current = null;
    if (!ancla?.el?.getBoundingClientRect) return;
    const desplazado = ancla.el.getBoundingClientRect().top - ancla.arriba;
    if (!desplazado) return;
    const scroller = contenedorDesplazable(ancla.el);
    if (scroller) scroller.scrollTop += desplazado;
  }, [abierta]);

  // CP-01-08: "cada dato faltante navega a SU CAMPO". Hasta ahora solo llevaba a la
  // seccion, y en la 6 —siete campos— eso deja al usuario buscando cual de ellos era.
  // Se abre la seccion, se deja que despliegue y se enfoca el control que `Field` marco
  // con `data-campo`.
  const irACampo = (x: Faltante) => {
    irA(x.seccion);
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-campo="${x.campo}"]`);
      if (!el) { refs.current[x.seccion]?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }, 90);
  };

  /**
   * EL ENVIO BLOQUEA MIENTRAS ESTA EN VUELO (reportado por el operador, 7-sep-2026).
   *
   * QUE PASABA. El boton de la ventana hacia `onEnviar(...)` y `setConfirmar(false)` en
   * la MISMA linea, sin esperar: la ventana se cerraba al instante y el formulario
   * quedaba vivo mientras la peticion viajaba. Y no es una peticion corta —fuerza un
   * guardado inmediato ANTES de enviar (`sincronizar`) y luego envia—, asi que la
   * rendija dura segundos. En ella se puede volver a pulsar "Enviar caso" y mandar el
   * caso dos veces.
   *
   * COMO SE CIERRA. La ventana NO se cierra hasta que la promesa termina, y mientras
   * tanto ensena un spinner. La ventana ya es una capa que cubre la pantalla entera
   * (`fixed inset-0`), asi que dejarla puesta bloquea el formulario de por si: no hace
   * falta deshabilitar el formulario campo a campo ni tapar nada aparte.
   *
   * ⚠ SPINNER SIN PORCENTAJE, como se pidio, y ademas es lo honesto: el servidor no
   * informa del avance, asi que cualquier barra que se pintara aqui seria inventada.
   *
   * Se cierran las TRES salidas de la ventana mientras dura: el boton Cancelar, el clic
   * en el fondo y la tecla Escape. Si una quedara abierta, el usuario volveria al
   * formulario con el envio todavia en curso, que es justo lo que se esta arreglando.
   *
   * `finally`: la ventana se cierra pase lo que pase. Si el envio va bien, la pantalla
   * de confirmacion la sustituye; si falla —duplicado, faltantes del servidor, error de
   * red— hay que salir para que se vea el aviso que explica por que.
   */
  const [enviando, setEnviando] = useState(false);
  const confirmarEnvio = async () => {
    if (enviando) return;                       // el doble clic en el propio boton
    setEnviando(true);
    try {
      await onEnviar?.({ ...c, id: numeroCaso ?? null });
    } catch {
      // ⚠ SE ATRAPA Y NO SE PINTA NADA AQUI, a proposito. Quien envia
      // (`intentarEnvio`, en el conector) ya clasifica el fallo y decide que aviso
      // sale: duplicado, faltantes del servidor o error de red. Repetirlo aqui daria
      // dos mensajes para un mismo hecho.
      //
      // Pero hay que ATRAPARLO. Con solo `finally`, un rechazo se escapa de esta
      // funcion `async` —cuya promesa nadie espera— y queda como "unhandled rejection":
      // ruido en la consola del navegador, y en las pruebas vitest lo cuenta como error
      // no controlado y devuelve codigo 1 aunque todas pasen. Fue lo que rompio el CI
      // del PR 155.
    } finally {
      setEnviando(false);
      setConfirmar(false);
    }
  };

  const enviar = () => {
    if (enviando) return;
    tocarTodo();
    if (!completo) {
      // El foco va al RESUMEN, no al primer campo: en un formulario de ocho secciones,
      // saltar al primer hueco esconde los otros doce. El resumen los dice todos y
      // desde el se salta a cada uno. Es la tecnica G139 de WCAG.
      setTimeout(() => resumen.current?.focus(), 0);
      return;
    }
    setConfirmar(true);
  };

  // El acordeon de secciones vive FUERA del componente (ver `Seccion`, arriba del archivo).
  // Todo lo que necesita viaja en `ctx`.
  const ctx = { abierta, alternar, intentoEnvio, porSeccion, refs, irACampo };

  // Props de validacion de un campo: el error que le toca —si ya toca ensenarlo— y el
  // enganche del `onBlur`. Se escribe una vez y se derrama en cada `Field` para que
  // anadir un campo obligatorio no sea anadir tres cosas y olvidarse de una.
  const validar = (campo: string) => ({ campo, error: errorDe(campo), onTocar: () => tocar(campo) });

  return (
    <>
      <PageHeader
        {...ENCABEZADO_DEL_REGISTRO}
        icon={FormatoVerde}
        actions={
          <>
            {guardado && (
              <span data-testid="registro-guardado" className="flex items-center gap-1.5 text-meta text-emerald-700 font-medium">
                <CircleCheck size={13} /> Guardado {comoHora(guardado, zonaDe(c.pais))}
              </span>
            )}
            <Button variant="secondary" icon={Save} onClick={() => { onGuardar?.({ ...c, id: numeroCaso ?? null }); setGuardado(new Date()); setSinEntregar(false); }}>
              Guardar borrador
            </Button>
            <Button variant="primary" icon={Send} onClick={enviar} disabled={enviando}>Enviar caso</Button>
          </>
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[15rem_minmax(0,1fr)] gap-5 items-start">
        {/* Indice de secciones: wayfinding del formulario largo */}
        <nav className="bg-white border border-hairline rounded-2xl p-3 lg:sticky lg:top-2" aria-label="Secciones del formulario">
          <p className="text-meta font-semibold text-ink-soft px-2 pb-2">
            Secciones
            {/* El "6 de 8" orienta mas que ocho iconos sueltos: dice cuanto falta
                de un vistazo, y celebra el avance sin ocupar espacio. */}
            <span className="float-right tabular-nums text-ink">
              {SECCIONES.filter((sk) => !sk.off && !(porSeccion[sk.key]?.length)).length}
              {' de '}
              {SECCIONES.filter((sk) => !sk.off).length}
            </span>
          </p>
          {SECCIONES.map((s) => (
            <ItemDelIndice
              key={s.key}
              s={s}
              faltan={porSeccion[s.key]?.length || 0}
              intentoEnvio={intentoEnvio}
              // Activa = la seccion en lectura, no «desplegada»: las ocho nacen
              // desplegadas y el indice las pintaba todas iguales (operador, 5-oct-2026).
              activa={seccionEnLectura === s.key}
              onIr={irA}
            />
          ))}
          <div className="mt-2 pt-2 border-t border-hairline px-2">
            <p className="text-meta font-semibold text-ink-soft">N.º de caso</p>
            <Codigo className="text-body text-ink">{numeroCaso}</Codigo>
            <p className="text-meta text-ink-soft mt-1">Correlativo por país</p>
          </div>
        </nav>

        <div className="space-y-3">
          {/* Resumen de faltantes AGRUPADO POR SECCION. `tabIndex={-1}` para poder llevarle
              el foco al fallar el envio, y `aria-live` para que el lector de pantalla lo
              anuncie si aparece sin que el foco se mueva.

              Agrupado y no como lista plana porque con doce faltantes la lista plana es un
              muro: por secciones se lee "en Medicamentos me faltan tres" y se decide por
              donde empezar. Cada linea sigue siendo un dato, no una seccion (HU-01). */}
          <div ref={resumen} tabIndex={-1} aria-live="polite" className="focus:outline-none">
            {intentoEnvio && cuantosFaltan > 0 && (
              <Alert tone="danger" title={tituloDeFaltantes(cuantosFaltan)}>
                <div className="mt-1.5 space-y-2">
                  {SECCIONES.filter((s) => porSeccion[s.key]?.length).map((s) => (
                    <div key={s.key}>
                      <p className="font-semibold">
                        <button type="button" onClick={() => irA(s.key)} className="underline hover:no-underline text-left min-h-6 py-0.5">
                          {s.n}. {s.label}
                        </button>
                        <span className="font-normal"> · {porSeccion[s.key].length}</span>
                      </p>
                      <ul className="ml-3 space-y-0.5">
                        {porSeccion[s.key].map((x) => (
                          <li key={x.campo}>
                            <button type="button" onClick={() => irACampo(x)} className="underline hover:no-underline text-left min-h-6 py-0.5">
                              {x.mensaje}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Alert>
            )}
          </div>

          {/* 1 · Encabezado */}
          <Seccion sk={SECCIONES[0]} ctx={ctx}>
            {/* Los paises salen de `/referencias/paises`, no de una constante del front.
                Cuando la peticion falla el desplegable queda vacio y hay que DECIRLO: la
                alternativa —caer de vuelta a una lista escrita aqui— es el defecto que
                este cambio cierra, porque volveria a haber dos copias del dato que decide
                autoridad, plazo y correlativo, y la copia solo se usaria cuando nadie
                esta mirando. */}
            {faltanPaises && (
              <Alert tone="warning" title="No se pudo cargar la lista de países">
                El país decide la autoridad, el plazo y el número del caso. Vuelve a cargar
                la pantalla; si sigue igual, avisa al administrador antes de llenar el formulario.
              </Alert>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* El rotulo decia "Lo asigna el sistema, por país" y era verdad a medias:
                  el sistema lo asigna AL ENVIAR (RN-01), no ahora. Lo que se ve mientras
                  el caso es borrador es el siguiente de la serie del país, leido de la
                  base — util para anticiparlo, pero no reservado a nadie. Decirlo evita
                  que el registrador lo apunte en su cuaderno antes de enviar. */}
              <Field
                label="Número de notificación"
                hint={numeroCaso
                  ? 'Previsto para este país. El definitivo se asigna al enviar'
                  : 'Lo asigna el sistema al enviar, por país'}
              >
                <Input value={numeroCaso ?? ''} placeholder="Se asigna al enviar" disabled />
              </Field>
              <Field label="Fecha de recepción" required hint="Inicia el reloj del plazo regulatorio" {...validar('fechaRecepcion')}>
                <Input type="date" value={c.fechaRecepcion} onChange={(e) => set('fechaRecepcion', e.target.value)} />
              </Field>
              <Field label="País" required {...validar('pais')}>
                {/* `registro-form-pais` (spec MG-ILE-370): el `Select` del catalogo todavia no
                    reenvia `data-testid` (lineamiento, Pendientes 1); llega al DOM con MG-ILE-710. */}
                {/* La opcion (bandera, nombre y autoridad como segunda linea) la arma
                    `SelectorDePais`, el mismo de todas las pantallas (operador, 5-oct-2026). */}
                <SelectorDePais data-testid="registro-form-pais" value={c.pais} onChange={(e) => set('pais', e.target.value)} placeholder="Seleccionar país..."
                  paises={PAISES} conAutoridad />
              </Field>
            </div>
          </Seccion>

          {/* 2 · Notificante y dueño */}
          <Seccion sk={SECCIONES[1]} ctx={ctx}>
            {/* Dos bloques independientes, cada uno en el ORDEN EXACTO del Formato Verde
                (filas 11-18 del Excel del alcance). Antes las dos columnas compartian las filas
                de la rejilla y quedaban emparejadas etiquetas que no se corresponden entre si
                ("Telefono/Email" del notificante junto a "Direccion" del cliente), lo que induce
                a error al transcribir.

                Cada bloque va en su propio panel. Se intento antes anclar "Profesion o rol" al pie
                de ambas columnas con `mt-auto`, pero como el bloque del notificante tiene un campo
                menos, el anclaje abria un hueco vacio en medio. Con panel propio las alturas
                distintas se leen como dos grupos, no como un descuadre. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-4 bg-canvas/60 border border-hairline rounded-xl p-4">
                <p className="text-compacto font-semibold text-ink">Datos del notificante</p>
                {/* Nombre de una persona: sin cifras ni simbolos, con punto para las
                    abreviaturas ("Dr.", "J. M."). T-44 §1, pedido del equipo tras
                    probar en dev — el campo aceptaba cualquier cosa y de cualquier largo. */}
                <Field label="Nombre" required hint="Elemento mínimo de una notificación válida" {...validar('notificante.nombre')}
                  contador={cuentaAtras(c.notificante.nombre, LIMITES.notificante_nombre_caracteres)}>
                  <Input value={c.notificante.nombre} maxLength={LIMITES.notificante_nombre_caracteres}
                    onChange={(e) => set('notificante.nombre', soloLetras(e.target.value, LIMITES.notificante_nombre_caracteres))}
                    placeholder="Nombre y apellidos" />
                </Field>
                {/* Texto libre: una direccion lleva cifras, comas y almohadillas. Solo tope. */}
                <Field label="Dirección" contador={cuentaAtras(c.notificante.direccion, LIMITES.direccion_caracteres)}>
                  <Input value={c.notificante.direccion} maxLength={LIMITES.direccion_caracteres}
                    onChange={(e) => set('notificante.direccion', conTope(e.target.value, LIMITES.direccion_caracteres))} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Teléfono" {...validar('notificante.telefono')}
                    contador={cuentaAtras(c.notificante.telefono, LIMITES.telefono_caracteres)}>
                    <Input inputMode="tel" value={c.notificante.telefono} maxLength={LIMITES.telefono_caracteres}
                      onChange={(e) => set('notificante.telefono', telefono(e.target.value, LIMITES.telefono_caracteres))} />
                  </Field>
                  <Field label="Email" {...validar('notificante.email')} pieDeDosLineas
                    hint={revisarCorreo(c.notificante.email, CORREO).sugerencia}>
                    {/* `normalizarCorreo` al SALIR y no al teclear: pasarlo a minusculas
                        en cada tecla mueve el cursor al final en varios navegadores. */}
                    <Input type="email" inputMode="email" value={c.notificante.email}
                      onChange={(e) => set('notificante.email', soloCorreo(e.target.value))}
                      onBlur={() => set('notificante.email', normalizarCorreo(c.notificante.email))} />
                  </Field>
                </div>
                {/* Nuevo respecto del prototipo: el backend acepta `institucion` y sin ella no
                    se sabe desde donde reporta el veterinario —clinica, fundo, distribuidor—,
                    que es lo que permite agrupar casos por origen. */}
                <Field label="Institución o empresa" hint="Desde dónde reporta: clínica, fundo, distribuidor..."
                  contador={cuentaAtras(c.notificante.institucion, LIMITES.institucion_caracteres)}>
                  <Input value={c.notificante.institucion} maxLength={LIMITES.institucion_caracteres}
                    onChange={(e) => set('notificante.institucion', conTope(e.target.value, LIMITES.institucion_caracteres))} />
                </Field>
                {/* AL PIE, como en el bloque del dueño (CP-02-03, pedido del operador 2-sep-2026):
                    los dos paneles terminan en "Profesion o rol" y se leen igual. Solo cambia el
                    orden de los dos `Field`; el panel es una columna flex y no hay rejilla que
                    descuadrar. */}
                <Field label="Profesión o rol"
                  hint={avisoRetirado(retirado('notificante.profesion_o_rol', c.notificante.rol), 'registro-form-notificante-rol-aviso-retirado')}>
                  <Select value={c.notificante.rol} onChange={(e) => set('notificante.rol', e.target.value)}
                    options={opcionesConRetirado(CATALOGOS.roles.map((o) => ({ value: o.id, label: o.label, icon: iconoRol(o.id) })),
                      retirado('notificante.profesion_o_rol', c.notificante.rol))}
                    placeholder="Seleccionar rol..." />
                </Field>
              </div>

              <div className="flex flex-col gap-4 bg-canvas/60 border border-hairline rounded-xl p-4">
                <p className="text-compacto font-semibold text-ink">Datos del cliente / dueño / paciente</p>
                {/* El caso mas frecuente: quien reporta ES el dueño. Copia los cinco campos y los
                    deja VISIBLES —no se quita nada—, solo bloqueados mientras la casilla siga
                    marcada. Mismo criterio que forma/concentracion/registro al elegir el SKU. */}
                <Checkbox checked={mismoQueNotificante}
                  onChange={(e) => {
                    setMismoQueNotificante(e.target.checked);
                    set('cliente.esElNotificante', e.target.checked);
                    if (e.target.checked) {
                      setC((p) => ({ ...p, cliente: { ...p.cliente, nombre: p.notificante.nombre,
                        direccion: p.notificante.direccion, telefono: p.notificante.telefono,
                        email: p.notificante.email, rol: p.notificante.rol } }));
                    }
                  }}
                  label="Los datos del dueño son los mismos del notificante" />
                {/* Nombre, direccion y telefono llevan el MISMO filtro y el MISMO tope que
                    su gemelo del notificante —el nombre comparte incluso la clave,
                    `notificante_nombre_caracteres`— porque son el mismo dato de la misma
                    persona: la casilla "son los mismos datos" los copia de un lado al
                    otro, y con dos reglas distintas el valor cabria aqui y no alla.
                    La razon social no tiene gemelo: es del dueño y solo de el. */}
                <Field label="Razón social" contador={cuentaAtras(String(c.cliente.razonSocial ?? ''), LIMITES.razon_social_caracteres)}>
                  <Input value={String(c.cliente.razonSocial ?? '')} maxLength={LIMITES.razon_social_caracteres}
                    onChange={(e) => set('cliente.razonSocial', conTope(e.target.value, LIMITES.razon_social_caracteres))} />
                </Field>
                <Field label="Nombre y apellido" contador={cuentaAtras(String(c.cliente.nombre ?? ''), LIMITES.notificante_nombre_caracteres)}>
                  <Input value={String(c.cliente.nombre ?? '')} disabled={mismoQueNotificante} maxLength={LIMITES.notificante_nombre_caracteres}
                    onChange={(e) => set('cliente.nombre', soloLetras(e.target.value, LIMITES.notificante_nombre_caracteres))} />
                </Field>
                <Field label="Dirección" contador={cuentaAtras(String(c.cliente.direccion ?? ''), LIMITES.direccion_caracteres)}>
                  <Input value={String(c.cliente.direccion ?? '')} disabled={mismoQueNotificante} maxLength={LIMITES.direccion_caracteres}
                    onChange={(e) => set('cliente.direccion', conTope(e.target.value, LIMITES.direccion_caracteres))} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Teléfono" {...validar('cliente.telefono')}
                    contador={cuentaAtras(String(c.cliente.telefono ?? ''), LIMITES.telefono_caracteres)}>
                    <Input inputMode="tel" value={String(c.cliente.telefono ?? '')} disabled={mismoQueNotificante}
                      maxLength={LIMITES.telefono_caracteres}
                      onChange={(e) => set('cliente.telefono', telefono(e.target.value, LIMITES.telefono_caracteres))} />
                  </Field>
                  <Field label="Email" {...validar('cliente.email')} pieDeDosLineas
                    hint={revisarCorreo(String(c.cliente.email ?? ''), CORREO).sugerencia}>
                    <Input type="email" inputMode="email" value={String(c.cliente.email ?? '')} disabled={mismoQueNotificante}
                      onChange={(e) => set('cliente.email', soloCorreo(e.target.value))}
                      onBlur={() => set('cliente.email', normalizarCorreo(String(c.cliente.email ?? '')))} />
                  </Field>
                </div>
                <Field label="Profesión o rol"
                  hint={avisoRetirado(retirado('duenio.profesion_o_rol', c.cliente.rol), 'registro-form-duenio-rol-aviso-retirado')}>
                  <Select value={String(c.cliente.rol ?? '')} onChange={(e) => set('cliente.rol', e.target.value)} disabled={mismoQueNotificante}
                    options={opcionesConRetirado(CATALOGOS.roles.map((o) => ({ value: o.id, label: o.label, icon: iconoRol(o.id) })),
                      retirado('duenio.profesion_o_rol', c.cliente.rol))}
                    placeholder="Seleccionar rol..." />
                </Field>
              </div>
            </div>
          </Seccion>

          {/* 3 · Identificación del EA */}
          <Seccion sk={SECCIONES[2]} ctx={ctx}>
            {/* `grupo`: son casillas, no un control unico. Con fieldset/legend el lector de
                pantalla anuncia de que va el grupo antes de leer las siete opciones, y el
                error del grupo tiene donde colgarse. El "tocado" lo marca el propio clic:
                en un grupo de casillas no hay un "salir del control" que valga. */}
            <Field grupo label="Categoría del efecto adverso" required {...validar('categorias')}
              hint="Marca todas las que apliquen. Al menos una es obligatoria">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                {CATALOGOS.categoriaEA.map((cat) => (
                  <Checkbox key={cat.id} checked={c.categorias.includes(cat.id)}
                    onChange={() => { tocar('categorias'); pedirCategoria(cat.id); }}
                    label={cat.label} tone={cat.l2 ? 'danger' : undefined} icon={ICONO_CATEGORIA[cat.id]}
                    hint={cat.l2 ? 'Habilita la sección de datos de la persona afectada' : undefined} />
                ))}
              </div>
            </Field>
            {afectaPersonas && (
              <div className="mt-4">
                <Alert tone="danger" title="Este caso involucra a una persona">
                  Se habilitó la <b>sección 7</b> con los datos de salud del afectado. Son datos sensibles: solo
                  los ven los roles autorizados y cada consulta queda registrada. La sección 4 sigue siendo la
                  del <b>animal</b>: si el evento afectó a los dos, regístralos ambos en el mismo caso.
                </Alert>
              </div>
            )}
          </Seccion>

          {/* 4 · Animales — "Descripcion del paciente(s)" en el Formato Verde.

              LA SECCION YA NO CONMUTA (decision del operador, 26-ago-2026).

              Hasta esta fecha, marcar "problemas de seguridad en personas" convertia esta seccion
              en la de la persona: fijaba la especie en HUMANO, cambiaba el catalogo de sexo y
              apagaba raza y estado fisiologico (nota E26 del Excel del alcance, AF §7b). La
              consecuencia era que un evento que afecta al animal Y a una persona —"tengo mi granja
              y me pinche", el ejemplo que levanto QA en la reunion de arranque— obligaba a abrir
              DOS Formatos Verdes para un solo hecho.

              El AF lo tenia declarado como PUNTO ABIERTO derivado al PO (§7b, HU-03) y el operador
              lo cierra asi: los dos se registran en el mismo caso. Esta seccion es SIEMPRE la del
              animal; la persona vive en la 7, que es otra tabla (`afectacion_persona`) colgando del
              mismo caso. El AF ya adelantaba que se podia: "la restriccion es de reglas de captura,
              no de datos". */}
          <Seccion sk={SECCIONES[3]} ctx={ctx}>
            {afectaPersonas && (
              <div className="mb-4">
                <Alert tone="info" title="Esta sección sigue siendo la del animal">
                  Marcaste afectación en personas, así que la <b>sección 7</b> recoge los datos de la persona.
                  Aquí se describe el <b>animal</b>, con todos sus campos activos. Un mismo evento puede
                  haber afectado a los dos, y ahora se registran juntos.
                </Alert>
              </div>
            )}
            {!c.animales.length && (
              <SeccionRepetibleVacia
                error={errorDe('animales')}
                invitacion="Aún no has agregado ningún animal afectado."
              />
            )}
            {/* SECCION REPETIBLE desde el 26-ago-2026, con el mismo patrón que Medicamentos.
                Los tres conteos del evento —tratados, con signos, muertos— NO se reparten
                por fila: siguen en la sección 6 porque en el Formato Verde son casillas del
                bloque del evento, y repartirlos entre animales sería inventar un dato que
                el notificante no dio. */}
            <div className="space-y-4">
              {c.animales.map((a, i) => (
                <div key={a.id ?? i} className="border border-hairline rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-canvas">
                    <span className="text-meta font-semibold text-ink">
                      Animal {i + 1}{a.especie ? ` · ${etiquetaDe(CATALOGOS.especies, a.especie)}` : ''}
                      {a.raza && <span className="text-ink-soft font-normal ml-2">{a.raza}</span>}
                    </span>
                    <button type="button" onClick={() => delAnimal(i)} className="p-1.5 rounded-lg text-ink-soft hover:bg-rose-50 hover:text-rose-600 transition-all" aria-label={`Eliminar animal ${i + 1}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Field label="Especie" required {...validar(`animales.${i}.especie`)}
                        hint={avisoRetirado(retirado(`animales[${i}].especie`, a.especie), 'registro-form-especie-aviso-retirado')}>
                        <Select value={a.especie} onChange={(e) => setAnimal(i, 'especie', String(e.target.value ?? ''))}
                          options={opcionesConRetirado(CATALOGOS.especies.map((o) => ({ value: o.id, label: o.label, icon: iconoEspecie(o.id) })),
                            retirado(`animales[${i}].especie`, a.especie))}
                          placeholder="Seleccionar especie..." />
                      </Field>
                      <Field label="Raza" contador={cuentaAtras(a.raza, LIMITES.animal_raza_caracteres)}>
                        {/* Sin cifras ni simbolos: es un nombre propio. Se conservan tildes,
                            guion y apostrofo — "Bretón", "Pequeño Münsterländer" son razas. */}
                        <Input value={a.raza} maxLength={LIMITES.animal_raza_caracteres}
                          onChange={(e) => setAnimal(i, 'raza', soloLetras(e.target.value, LIMITES.animal_raza_caracteres))} />
                      </Field>
                      {/* `grupo` (HU-51, tarea 51-7): son radios, no un control unico. Sin el,
                          `Field` ataba la etiqueta a la caja de las opciones —que no es un
                          control— y el grupo quedaba sin nombre: el lector decia «Macho, botón
                          de opción» sin decir de que. Igual en Sitio de la inyección, el sexo de
                          la persona y la atención médica. */}
                      <Field label="Sexo" grupo>
                        <div className="flex flex-wrap gap-2">
                          {CATALOGOS.sexo.map((x) => (
                            <Radio key={x} name={`sexo${i}`} value={x} checked={a.sexo === x}
                              onChange={(e) => setAnimal(i, 'sexo', String(e.target.value ?? ''))} label={enMayuscula(x)} />
                          ))}
                        </div>
                      </Field>
                      <Field label="Estado fisiológico"
                        hint={avisoRetirado(retirado(`animales[${i}].estado_fisiologico`, a.estadoFisiologico), 'registro-form-estado-fisiologico-aviso-retirado')}>
                        <Select value={a.estadoFisiologico} onChange={(e) => setAnimal(i, 'estadoFisiologico', String(e.target.value ?? ''))}
                          options={opcionesConRetirado(CATALOGOS.estadoFisiologico.map((o) => ({ value: o.id, label: o.label })),
                            retirado(`animales[${i}].estado_fisiologico`, a.estadoFisiologico))} placeholder="Seleccionar..." />
                      </Field>
                      {/* `validar()` le faltaba: sin el no hay `data-campo` —el resumen de
                          faltantes no puede llevarle el foco— ni aviso al salir del campo
                          (RNF-09 y RNF-11). Se anade con la regla de la edad minima. */}
                      <Field label="Edad" hint="En años" {...validar(`animales.${i}.edad`)}
                        contador={cuentaAtras(a.edad, LIMITES.animal_edad_digitos, 'dígitos')}>
                        <Input inputMode="numeric" value={a.edad} placeholder="ej. 3"
                          onChange={(e) => setAnimal(i, 'edad', soloEnteros(e.target.value, LIMITES.animal_edad_digitos))} />
                      </Field>
                      {/* `validar()` le faltaba: sin el no hay `data-campo` —el resumen de
                          faltantes no puede llevarle el foco— ni aviso al salir del campo.
                          Se anade con la regla del "mayor que 0" (7-sep-2026). */}
                      <Field label="Peso" hint="En kilos. Admite decimales" {...validar(`animales.${i}.peso`)}
                        contador={cuentaDigitos(a.peso, LIMITES.animal_peso_digitos)}>
                        {/* Solo el numero: la unidad ya la dice la etiqueta. Con "420 kg"
                            dentro del valor no se puede comparar el peso entre casos.
                            Sin `maxLength`: el tope cuenta DIGITOS y el atributo cuenta
                            caracteres — con el punto de por medio cortaria antes de tiempo. */}
                        <Input inputMode="decimal" value={a.peso} placeholder="ej. 420"
                          onChange={(e) => setAnimal(i, 'peso', pesoDecimal(e.target.value, LIMITES.animal_peso_digitos))} />
                      </Field>
                    </div>
                    <Field label="Motivo de tratamiento" className="mt-4"
                      hint="Por qué se administró el medicamento"
                      contador={cuentaAtras(a.motivo, LIMITES.animal_motivo_caracteres)}>
                      <Textarea rows={2} value={a.motivo} maxLength={LIMITES.animal_motivo_caracteres}
                        onChange={(e) => setAnimal(i, 'motivo', conTope(e.target.value, LIMITES.animal_motivo_caracteres))} />
                    </Field>
                    {/* CONTEOS DE ESTE GRUPO (HU-03, pedido del equipo de pruebas 2-sep-2026).
                        Los TOTALES del evento siguen en la seccion 6 y son los que deciden la
                        gravedad; estos dicen cuantos de este animal o grupo se trataron,
                        enfermaron y murieron. Mismo filtro, mismo tope de digitos y misma
                        coherencia (muertos <= con signos <= tratados) que los de la seccion 6.

                        VAN DESPUES DE "Motivo de tratamiento" (3-sep-2026): es el orden del
                        Formato Verde, que es el que la tabla de HU-03 declara y el que el
                        registrador tiene delante en el papel. Estuvieron un dia entre Peso y
                        Motivo, para completar la fila de la rejilla; la comodidad de la
                        maquetacion no manda sobre el orden de transcripcion. En su propia
                        rejilla de tres columnas: las tres celdas llenan una fila exacta y
                        quedan alineadas con Especie / Raza / Sexo de arriba. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      {/* CP-08-05 · el `{...validar()}` le faltaba a esta y no a sus dos
                          vecinas, un olvido de cuando se anadieron los tres conteos
                          (2-sep-2026). Sin el no hay `onTocar`, y salir del campo no
                          marcaba nada: RNF-09 pide validar AL SALIR, y el caso de prueba
                          mide exactamente ese gesto. */}
                      <Field label="N.º tratados" hint="De este grupo" {...validar(`animales.${i}.tratados`)}
                        contador={cuentaAtras(a.tratados, LIMITES.evento_conteos_digitos, 'dígitos')}>
                        <Input inputMode="numeric" value={a.tratados}
                          onChange={(e) => setAnimal(i, 'tratados', soloEnteros(e.target.value, LIMITES.evento_conteos_digitos))} />
                      </Field>
                      <Field label="N.º con signos" hint="De este grupo" {...validar(`animales.${i}.conSignos`)}
                        contador={cuentaAtras(a.conSignos, LIMITES.evento_conteos_digitos, 'dígitos')}>
                        <Input inputMode="numeric" value={a.conSignos}
                          onChange={(e) => setAnimal(i, 'conSignos', soloEnteros(e.target.value, LIMITES.evento_conteos_digitos))} />
                      </Field>
                      <Field label="N.º muertos" hint="De este grupo" {...validar(`animales.${i}.muertos`)}
                        contador={cuentaAtras(a.muertos, LIMITES.evento_conteos_digitos, 'dígitos')}>
                        <Input inputMode="numeric" value={a.muertos}
                          onChange={(e) => setAnimal(i, 'muertos', soloEnteros(e.target.value, LIMITES.evento_conteos_digitos))} />
                      </Field>
                    </div>
                    {/* Nuevo respecto del prototipo. El backend acepta `estado_salud` y la
                        causalidad lo necesita: un animal que ya estaba enfermo antes de
                        recibir el medicamento es el primer factor de confusión que el
                        Comité descarta o confirma. */}
                    <Field label="Estado de salud antes del tratamiento" className="mt-4"
                      hint="Cómo estaba el animal ANTES de recibir el medicamento"
                      contador={cuentaAtras(a.estadoSalud, LIMITES.animal_estado_salud_caracteres)}>
                      <Textarea rows={2} value={a.estadoSalud} maxLength={LIMITES.animal_estado_salud_caracteres}
                        onChange={(e) => setAnimal(i, 'estadoSalud', conTope(e.target.value, LIMITES.animal_estado_salud_caracteres))} />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="secondary" icon={Plus} onClick={addAnimal} className="mt-3" data-campo="animales">
              Agregar animal
            </Button>
          </Seccion>

          {/* 5 · Medicamentos (repetible) */}
          <Seccion sk={SECCIONES[4]} ctx={ctx}>
            {maestro.fallo && (
              <Alert tone="warning" title="El maestro de productos no está disponible">
                No se pudo cargar la lista de productos: el buscador saldrá vacío y el caso no
                podrá enviarse hasta que se cargue. Avisa al administrador antes de seguir llenando.
              </Alert>
            )}
            {/* HU-29-C8: el pais del caso no ofrece ningun producto. El texto es el del servidor. */}
            {!maestro.fallo && !maestro.cargando && !maestro.productos.length && maestro.textoVacio && (
              <div className="mb-4">
                <Alert tone="info" data-testid="registro-form-producto-vacio">{maestro.textoVacio}</Alert>
              </div>
            )}
            {!c.medicamentos.length && (
              <SeccionRepetibleVacia
                error={errorDe('medicamentos')}
                invitacion="Aún no has agregado ningún medicamento sospechoso."
              />
            )}
            <div className="space-y-4">
              {c.medicamentos.map((m, i) => (
                <div key={m.id} className="border border-hairline rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-canvas">
                    <span className="text-meta font-semibold text-ink">
                      Medicamento {i + 1}{m.nombre ? ` · ${m.nombre}` : ''}
                      {m.lote && <Mono className="text-meta text-ink-soft ml-2">lote {m.lote}</Mono>}
                    </span>
                    <button type="button" onClick={() => delMed(i)} className="p-1.5 rounded-lg text-ink-soft hover:bg-rose-50 hover:text-rose-600 transition-all" aria-label={`Eliminar medicamento ${i + 1}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Se usa el mismo Select buscable que el resto de la app en vez de un buscador
                        a medida. El anterior solo mostraba resultados DESPUES de escribir algo, asi
                        que no habia forma de saber que productos existen: al abrirlo se veia un campo
                        vacio. Ahora al pulsarlo despliega el maestro completo y escribir lo filtra. */}
                    <Field label="Nombre comercial del producto" required hint="Del maestro de productos" {...validar(`medicamentos.${i}.sku`)}>
                      <Select value={m.sku} onChange={(e) => elegirSku(i, String(e.target.value ?? ''))} buscable
                        data-testid="registro-form-producto"
                        placeholder="Elige un producto o escribe para buscar..."
                        options={maestro.productos.map((p) => ({
                          value: p.sku, label: p.nombre, icon: <Vial size={16} className="text-ink-soft shrink-0" />,
                          // CC2-04-C1: con varias autoridades, cada registro con la suya.
                          hint: [p.sku, `${p.forma} ${p.concentracion}`.trim(), registrosDeLaOpcion(p)].filter(Boolean).join(' · '),
                          // HU-29-C9: las semillas que se ofrecen mientras el pais no tiene filas
                          // llevan el rotulo «dato de prueba» junto al SKU y al registro.
                          marca: p.esDatoDePrueba
                            ? <span data-testid="registro-form-producto-dato-de-prueba">{ROTULO_DATO_DE_PRUEBA}</span>
                            : undefined,
                        }))} />
                    </Field>
                    {/* Estos dos campos SIEMPRE se muestran, aunque aun no se haya elegido el SKU.
                        Antes solo aparecian despues de elegirlo y como texto suelto: al abrir la
                        seccion no se veia que existieran, y en el formulario en papel son dos
                        lineas mas que rellenar. Van desactivados porque los trae el maestro
                        (nota de la fila 30 del Excel: "hasta aqui viene de la base de datos
                        info de SKU proveniente de la etiqueta"). */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Forma farmacéutica y concentración" hint={m.sku ? 'Del maestro de productos' : 'Se completa al elegir el producto'}>
                        <Input value={m.sku ? [m.forma, m.concentracion].filter(Boolean).join(' · ') : ''} disabled placeholder="—" />
                      </Field>
                      <Field label="Número de registro sanitario" hint={m.sku ? 'Del maestro de productos' : 'Se completa al elegir el producto'}>
                        <Input value={m.sku ? m.registro : ''} disabled placeholder="—" className="font-mono" />
                      </Field>
                    </div>
                    {/* De aqui en adelante, el orden es el del Formato Verde (filas 31-38). */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Número de lote" contador={cuentaAtras(m.lote, LIMITES.medicamento_lote_caracteres)}>
                        <Input value={m.lote} maxLength={LIMITES.medicamento_lote_caracteres} placeholder="ej. L-2026-0451"
                          onChange={(e) => setMed(i, 'lote', alfanumerico(e.target.value, LIMITES.medicamento_lote_caracteres))} />
                      </Field>
                      <Field label="Fecha de vencimiento"><Input type="date" value={m.vencimiento} onChange={(e) => setMed(i, 'vencimiento', String(e.target.value ?? ''))} /></Field>
                    </div>
                    {/* El prototipo tenia UN campo con "dosis, frecuencia y via" juntas. Se lee bien
                        en papel y es inservible en base: nadie puede filtrar por via ni comparar
                        frecuencias entre casos si las tres viven en una frase. El backend ya las
                        tiene separadas; la pantalla las separa tambien. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Dosis" contador={cuentaAtras(m.dosis, LIMITES.medicamento_dosis_caracteres)}>
                        <Input value={m.dosis} placeholder="ej. 1 mL/50 kg" maxLength={LIMITES.medicamento_dosis_caracteres}
                          onChange={(e) => setMed(i, 'dosis', alfanumerico(e.target.value, LIMITES.medicamento_dosis_caracteres))} />
                      </Field>
                      <Field label="Vía de administración" contador={cuentaAtras(m.via, LIMITES.medicamento_via_caracteres)}>
                        <Input value={m.via} placeholder="ej. subcutánea" maxLength={LIMITES.medicamento_via_caracteres}
                          onChange={(e) => setMed(i, 'via', alfanumerico(e.target.value, LIMITES.medicamento_via_caracteres))} />
                      </Field>
                      <Field label="Frecuencia" contador={cuentaAtras(m.frecuencia, LIMITES.medicamento_frecuencia_caracteres)}>
                        <Input value={m.frecuencia} placeholder="ej. c/24 h" maxLength={LIMITES.medicamento_frecuencia_caracteres}
                          onChange={(e) => setMed(i, 'frecuencia', alfanumerico(e.target.value, LIMITES.medicamento_frecuencia_caracteres))} />
                      </Field>
                    </div>
                    {/* Nuevas respecto del prototipo: alimentan la secuencia temporal de la
                        causalidad (F-03, Sprint 2). Sin ellas el Comite no puede establecer
                        cronologia y hay que volver a preguntarle al notificante semanas despues. */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Duración del tratamiento" contador={cuentaAtras(m.duracion, LIMITES.medicamento_duracion_caracteres)}>
                        <Input value={m.duracion} placeholder="ej. 3 días" maxLength={LIMITES.medicamento_duracion_caracteres}
                          onChange={(e) => setMed(i, 'duracion', alfanumerico(e.target.value, LIMITES.medicamento_duracion_caracteres))} />
                      </Field>
                      <Field label="Inicio de la administración" hint="Alimenta la cronología de la causalidad">
                        <Input type="date" value={m.fechaInicio} onChange={(e) => setMed(i, 'fechaInicio', String(e.target.value ?? ''))} />
                      </Field>
                      <Field label="Fin de la administración">
                        <Input type="date" value={m.fechaFin} onChange={(e) => setMed(i, 'fechaFin', String(e.target.value ?? ''))} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Condiciones de almacenamiento" contador={cuentaAtras(m.almacenamiento, LIMITES.medicamento_almacenamiento_caracteres)}>
                        <Input value={m.almacenamiento} placeholder="ej. Refrigerado 2-8 °C" maxLength={LIMITES.medicamento_almacenamiento_caracteres}
                          onChange={(e) => setMed(i, 'almacenamiento', alfanumerico(e.target.value, LIMITES.medicamento_almacenamiento_caracteres))} />
                      </Field>
                      <Field label="Quién administró el medicamento">
                        <Select value={m.administro} onChange={(e) => setMed(i, 'administro', String(e.target.value ?? ''))}
                          options={CATALOGOS.roles.map((o) => ({ value: o.id, label: o.label, icon: iconoRol(o.id) }))}
                          placeholder="Seleccionar..." />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Obligatorio para enviar (AF §7b y el trigger `tg_caso_exige_causalidad`
                          de la base). El asterisco faltaba, asi que el unico aviso llegaba al
                          pulsar enviar — con el formulario entero ya lleno. */}
                      <Field grupo required label="¿Cree que el efecto adverso se debe al medicamento?"
                        {...validar(`medicamentos.${i}.esCausa`)}>
                        <div className="flex flex-wrap gap-2">
                          {['Sí', 'No'].map((o) => (
                            <Radio key={o} name={`causa${i}`} value={o} checked={m.esCausa === o}
                              onChange={(e) => { tocar(`medicamentos.${i}.esCausa`); setMed(i, 'esCausa', String(e.target.value ?? '')); }} label={o} />
                          ))}
                        </div>
                      </Field>
                      {/* La ayuda ya no pide una accion al usuario ("agrega otro
                          medicamento"): describe lo que va a pasar, porque el bloque lo
                          abre la propia respuesta. */}
                      <Field grupo label="¿Se administró más medicamentos simultáneamente?"
                        hint="Al responder Sí se abre el bloque del siguiente medicamento"
                        {...validar(`medicamentos.${i}.masMedicamentos`)}>
                        <div className="flex flex-wrap gap-2">
                          {['Sí', 'No'].map((v) => <Radio key={v} name={`mas${i}`} value={v} checked={m.masMedicamentos === v} onChange={(e) => responderMasMedicamentos(i, e.target.value)} label={v} />)}
                        </div>
                      </Field>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="secondary" icon={Plus} onClick={addMed} className="mt-3">Agregar medicamento</Button>
          </Seccion>

          {/* 6 · Evento adverso */}
          <Seccion sk={SECCIONES[5]} ctx={ctx}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* OBLIGATORIA AL ENVIAR (CP-05-03, 3-sep-2026). El servidor ya la exigia
                  —`EventoAdversoEntrada` la declara requerida, y sin ella el bloque del
                  evento no llega—, pero el formulario no la pedia: ni asterisco, ni
                  faltante, ni aviso. El registrador llenaba las ocho secciones, pulsaba
                  enviar y ahi descubria que faltaba. Es el fallo que HU-01 existe para
                  evitar, y el mismo criterio que su vecino "Detalle de la reaccion". */}
              {/* El aviso de fecha futura es SUGERENCIA, no error (CP-05-09): se pinta
                  bajo el campo y no impide guardar ni enviar. Mismo canal que las
                  sugerencias de dominio del correo del notificante. Sin `max` en el
                  input a proposito: taparia el calendario en silencio en vez de avisar,
                  y el caso de prueba necesita poder elegir una fecha futura para ver el
                  aviso. */}
              <Field label="Fecha de inicio del evento" required {...validar('evento.inicio')} pieDeDosLineas
                hint={revisarFechaDeInicio(c.evento.inicio, zonaDe(c.pais)).sugerencia}>
                <Input type="date" value={c.evento.inicio} onChange={(e) => set('evento.inicio', e.target.value)} />
              </Field>
              {/* `validar()` les faltaba: sin el no hay `data-campo` —el resumen de
                  faltantes no puede llevarles el foco— ni aviso al salir del campo. Se
                  anade con la regla del "mayor que 0" (7-sep-2026). */}
              <Field label="Tiempo entre administración y aparición" {...validar('evento.tiempoAparicion')}
                contador={cuentaAtras(c.evento.tiempoAparicion, LIMITES.evento_tiempo_aparicion_digitos, 'dígitos')}>
                {/* SIN `flex-wrap`: no es un grupo de opciones, es UN control partido en
                    numero y unidad. Envolverlo deja la unidad debajo y descuadra la fila. */}
                <div className="flex gap-2">
                  {/* `type="text"` con `inputMode="numeric"`, no `type="number"`: el
                      numerico del navegador deja escribir "e", "+" y "-" —son notacion
                      cientifica valida— y ademas ignora `maxLength`. El filtro de
                      `soloEnteros` cubre las dos cosas. */}
                  <div className="flex-1 min-w-0">
                    {/* HU-51 (WCAG 4.1.2): `Field` ata su etiqueta al `div` que envuelve el par
                        numero + unidad, no a los controles: cada uno lleva su propio nombre. */}
                    <Input inputMode="numeric" value={c.evento.tiempoAparicion}
                      aria-label="Tiempo entre administración y aparición"
                      onChange={(e) => set('evento.tiempoAparicion', soloEnteros(e.target.value, LIMITES.evento_tiempo_aparicion_digitos))} />
                  </div>
                  <Select value={c.evento.unidadAparicion} onChange={(e) => set('evento.unidadAparicion', e.target.value)}
                    aria-label="Unidad del tiempo de aparición"
                    options={CATALOGOS.unidadTiempo} className="!w-28 shrink-0" />
                </div>
              </Field>
              <Field label="Duración del efecto adverso" {...validar('evento.duracion')}
                contador={cuentaAtras(c.evento.duracion, LIMITES.evento_duracion_digitos, 'dígitos')}>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Input inputMode="numeric" value={c.evento.duracion}
                      aria-label="Duración del efecto adverso"
                      onChange={(e) => set('evento.duracion', soloEnteros(e.target.value, LIMITES.evento_duracion_digitos))} />
                  </div>
                  <Select value={c.evento.unidadDuracion} onChange={(e) => set('evento.unidadDuracion', e.target.value)}
                    aria-label="Unidad de la duración"
                    options={CATALOGOS.unidadTiempo} className="!w-28 shrink-0" />
                </div>
              </Field>
            </div>
            {/* El prototipo tenia UN campo de texto libre "Severidad o desenlace". El backend
                distingue dos cosas: `desenlace` con ESCALA —la del Formato Verde— y
                `severidad` en texto. Se separan porque solo la escala es agregable: con texto
                libre, "murio", "muerte" y "falleció" son tres desenlaces distintos y el KPI de
                mortalidad no se puede calcular.

                Las opciones salen del CATALOGO (`desenlace_evento`), no de una lista escrita
                aqui. Estuvieron en duro —y a la vez en un CHECK de la base y en un enum de
                Python— hasta el 25-ago-2026: tres copias de la misma escala que obligaban a
                redesplegar la SPA para añadir un desenlace. Lo que se guarda es `o.id`, el
                codigo, porque la columna es FK a ese catalogo. */}
            <Field label="Desenlace" grupo className="mt-4"
              hint="Escala del Formato Verde. Es el campo que alimenta los indicadores">
              {CATALOGOS.desenlaces.length ? (
                <div className="flex flex-wrap gap-2">
                  {CATALOGOS.desenlaces.map((o) => (
                    // `aria-pressed` (WCAG 4.1.2, HU-51): la opcion elegida se decia solo con el color.
                    <button key={o.id} type="button" onClick={() => set('evento.desenlace', o.id)}
                      aria-pressed={c.evento.desenlace === o.id}
                      className={'px-3.5 py-2 rounded-lg border text-meta transition-all min-h-9 '
                        + (c.evento.desenlace === o.id
                          ? 'bg-marca-bruma/50 border-marca text-marca-texto font-semibold'
                          : 'bg-white border-control-borde text-ink-soft hover:border-control-borde-fuerte')}>
                      {o.label}
                    </button>
                  ))}
                  {retirado('evento_adverso.desenlace', c.evento.desenlace) && (
                    <p data-testid="registro-form-desenlace-aviso-retirado" className="basis-full text-meta text-amber-800">
                      {retirado('evento_adverso.desenlace', c.evento.desenlace)?.texto_del_valor} · {retirado('evento_adverso.desenlace', c.evento.desenlace)?.aviso}
                    </p>
                  )}
                </div>
              ) : (
                // Sin lista NO se cae de vuelta a una escrita aqui: esa copia es justo lo que
                // este cambio elimina, y solo se leeria el dia que la peticion falla.
                <p className="text-meta text-ink-soft">
                  No se pudo cargar la escala de desenlaces. El resto del formulario se puede
                  llenar igual; avisa al administrador antes de enviar.
                </p>
              )}
            </Field>
            <Field label="Severidad · detalle" className="mt-4"
              hint="Matiz que la escala no recoge: cuántos, cómo evolucionaron, qué tratamiento"
              contador={cuentaAtras(c.evento.severidad, LIMITES.evento_severidad_caracteres)}>
              <Input value={c.evento.severidad} maxLength={LIMITES.evento_severidad_caracteres}
                placeholder="ej. dos murieron; el resto se recuperó con soporte"
                onChange={(e) => set('evento.severidad', conTope(e.target.value, LIMITES.evento_severidad_caracteres))} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {/* SOLO LECTURA desde el 3-sep-2026: es la suma de "N.º tratados" de los
                  animales de la seccion 4. `readOnly` y no `disabled` a proposito: el
                  teclado y el lector de pantalla llegan al campo y leen el total y su
                  ayuda; solo no se puede escribir. Sin contador: nadie teclea aqui. */}
              <Field label="N.º de animales tratados" hint="Suma de N.º tratados de los animales de la sección 4">
                <Input inputMode="numeric" value={c.evento.tratados} readOnly
                  className="read-only:bg-slate-50 read-only:text-ink-soft read-only:cursor-default" />
              </Field>
              {/* Suma de los animales, PERO editable (7-sep-2026): escribir aqui congela
                  el valor para el resto de la edicion. Ver el efecto de `sumaDeConSignos`. */}
              <Field label="N.º de animales con sintomatología" {...validar('evento.conSignos')}
                hint="Suma de N.º con signos de los animales; puedes corregirlo"
                contador={cuentaAtras(c.evento.conSignos, LIMITES.evento_conteos_digitos, 'dígitos')}>
                <Input inputMode="numeric" value={c.evento.conSignos}
                  onChange={(e) => {
                    totalesTocados.current.conSignos = true;
                    set('evento.conSignos', soloEnteros(e.target.value, LIMITES.evento_conteos_digitos));
                  }} />
              </Field>
              <Field label="N.º de animales muertos" {...validar('evento.muertos')}
                hint="Suma de N.º muertos de los animales; puedes corregirlo"
                contador={cuentaAtras(c.evento.muertos, LIMITES.evento_conteos_digitos, 'dígitos')}>
                <Input inputMode="numeric" value={c.evento.muertos}
                  onChange={(e) => {
                    totalesTocados.current.muertos = true;
                    set('evento.muertos', soloEnteros(e.target.value, LIMITES.evento_conteos_digitos));
                  }} />
              </Field>
            </div>
            <Field label="Detalle de la reacción" required className="mt-4" {...validar('evento.detalle')}
              hint="Indica si el efecto adverso se trató, cómo, con qué y cuál fue el resultado"
              contador={cuentaAtras(c.evento.detalle, LIMITES.evento_detalle_caracteres)}>
              <Textarea rows={4} value={c.evento.detalle} maxLength={LIMITES.evento_detalle_caracteres}
                data-testid="registro-form-evento-detalle"
                onChange={(e) => set('evento.detalle', conTope(e.target.value, LIMITES.evento_detalle_caracteres))} />
            </Field>

            {/* Reunion 01: quien registra es veterinario y puede dar su criterio de gravedad.
                Es un CRITERIO PRELIMINAR, no la decision: esa la sigue tomando el Comite. Se
                separa en dos campos distintos (`graveSugerido` y `grave`) para que nunca se
                confundan — si compartieran campo, la primera impresion de campo se leeria como
                la clasificacion formal en el informe y ante la autoridad.

                Las respuestas salen del catalogo `criterio_preliminar` (25-ago-2026): eran dos
                literales escritos aqui, con un CHECK en la base y un enum en el backend
                diciendo lo mismo. `graveSugerido` guarda ahora el CODIGO tal cual —ya no hay
                traduccion 'si'->'grave' en el mapeo—, que es lo que la FK acepta.

                LAS TRES son filas del catalogo, "Prefiero no opinar" incluida. Tuvo un boton
                fijo aparte durante un rato, y se quito por lo mismo que se quitaron los otros
                dos literales: con un boton en duro, agregar una cuarta respuesta obligaria a
                volver a esta pantalla. Aqui no queda ninguna respuesta escrita a mano. */}
            <Field label="¿Te parece grave?" grupo className="mt-4"
              hint="Tu criterio como veterinario. La decisión formal la toma el Comité de Farmacovigilancia">
              <div className="flex flex-wrap gap-2">
                {CATALOGOS.criteriosPreliminares.map((o) => (
                  <button key={o.id} type="button"
                    // Volver a pulsar la respuesta elegida la deselecciona: sin eso, una vez
                    // contestada la pregunta no habria forma de dejarla en blanco otra vez.
                    onClick={() => set('graveSugerido', c.graveSugerido === o.id ? '' : o.id)}
                    aria-pressed={c.graveSugerido === o.id}
                    className={'px-3.5 py-2 rounded-lg border text-meta transition-all min-h-9 '
                      + (c.graveSugerido === o.id
                        ? 'bg-marca-bruma/50 border-marca text-marca-texto font-semibold'
                        : 'bg-white border-control-borde text-ink-soft hover:border-control-borde-fuerte')}>
                    {o.label}
                  </button>
                ))}
                {retirado('criterio_preliminar', c.graveSugerido) && (
                  <p data-testid="registro-form-criterio-preliminar-aviso-retirado" className="basis-full text-meta text-amber-800">
                    {retirado('criterio_preliminar', c.graveSugerido)?.texto_del_valor} · {retirado('criterio_preliminar', c.graveSugerido)?.aviso}
                  </p>
                )}
              </div>
            </Field>
            {c.graveSugerido === CRITERIO_QUE_AVISA_AL_COMITE && (
              <Alert tone="warning" title="Queda señalado para el Comité">
                El caso llegará marcado con tu criterio para que el Comité lo mire primero. La clasificación
                oficial y el plazo ante la autoridad los decide el Comité.
              </Alert>
            )}

            <div className="mt-5 pt-4 border-t border-hairline">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-compacto font-semibold text-ink">
                  Otra información relevante · adjuntos <span className="text-rose-700">*</span>
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" icon={Paperclip} onClick={addEvidencia}
                    disabled={Boolean(subiendoAdjunto)} data-campo="evidencias"
                    data-testid="registro-evidencias-adjuntar">
                    {subiendoAdjunto ? 'Subiendo…' : 'Adjuntar'}
                  </Button>
                </div>
              </div>
              {/* El selector real. Oculto y disparado por el boton: el `<input type="file">`
                  nativo no se puede tipografiar, y un boton de la casa junto a un control
                  del sistema operativo se ve siempre mal.
                  `accept` NO lleva lista blanca: el cliente pidio "cualquier tipo de archivo"
                  y lo que se bloquea son los ejecutables, que van por lista negra en
                  `parametro` (HU-31) y los comprueba el servidor.
                  Los `data-testid` de esta seccion los fijan `ui/MG-ILE-380.md` y
                  `ui/MG-ILE-377.md`. Los dos avisos llevan el suyo en el envoltorio porque
                  `Alert` todavia no reenvia el atributo (tarea 51-1). */}
              <input ref={selectorDeArchivo} type="file" className="hidden" multiple
                onChange={alElegirArchivo} aria-hidden="true" tabIndex={-1}
                data-testid="registro-form-evidencias" />

              {/* CP-07-19. Va ANTES del boton y no despues: es una pregunta sobre el
                  archivo que se va a elegir, y una vez pulsado «Adjuntar» el selector del
                  sistema tapa la pantalla y ya no hay donde responderla.
                  No se muestra cuando el caso YA declara afectacion a personas: ahi todas
                  las evidencias van al contenedor L2 de todas formas, y ofrecer una
                  casilla que no cambia nada solo confunde. */}
              {!afectaPersonas && (
                <div className="mb-2">
                  <Checkbox
                    checked={evidenciaConDatoDePersonas}
                    onChange={() => setEvidenciaConDatoDePersonas((antes) => !antes)}
                    label="Esta evidencia contiene datos de personas"
                    hint="Historias clínicas, informes médicos o fotografías de una persona afectada. Se guarda con acceso restringido y auditado, y caduca a los 5 años."
                    tone="danger"
                  />
                </div>
              )}

              {subiendoAdjunto && (
                <div className="mb-2">
                  <Alert tone="info">Subiendo <b>{String(subiendoAdjunto)}</b> al almacenamiento…</Alert>
                </div>
              )}
              {/* Los rechazos son de PRE-VALIDACION —tope de tamano, extension, duplicado—
                  y son cortesia: el servidor valida igual. Se listan todos, no solo el
                  primero, porque un archivo puede fallar por dos motivos a la vez. */}
              {/* #377-C2: la seleccion no cabe y no se subio ninguno (todo o nada). El texto es
                  el `detalle` del 422 del lote. */}
              {cupoExcedidoDeAdjunto && (
                <div className="mb-2">
                  <Alert tone="warning" title="No se adjuntó ningún archivo" data-testid="registro-evidencias-cupo-excedido">
                    {cupoExcedidoDeAdjunto}
                  </Alert>
                </div>
              )}
              {Boolean(rechazosDeAdjunto?.length) && (
                <div className="mb-2" data-testid="registro-evidencias-rechazos">
                  <Alert tone="warning" title="No se pudo adjuntar">
                    <ul className="mt-1 space-y-0.5">
                      {(rechazosDeAdjunto ?? []).map((motivo) => (
                        <li key={motivo} data-testid="registro-evidencias-rechazo">{motivo}</li>
                      ))}
                    </ul>
                  </Alert>
                </div>
              )}
              {/* HU-07, "la subida falla": se indica y se CONSERVA el resto del formulario.
                  Por eso es un aviso dentro de la seccion y no una pantalla de error. */}
              {Boolean(falloDeAdjunto) && (
                <div className="mb-2" data-testid="registro-evidencias-error-envio">
                  <Alert tone="danger" title="Falló la subida">
                    {aFallo(falloDeAdjunto).message || 'No se pudo subir el archivo. Vuelve a intentarlo.'}
                    {' '}El resto del formulario sigue como estaba.
                  </Alert>
                </div>
              )}

              <ListaDeEvidencias
                evidencias={evidencias}
                sePuedenQuitar
                onQuitar={quitarEvidencia}
                quitando={quitandoAdjunto}
                error={errorDe('evidencias')}
                limites={limitesDeAdjuntos}
                presupuestoDelServidor={presupuestoDeAdjuntos}
              />
            </div>
          </Seccion>

          {/* 7 · EA en personas (condicional, L2) */}
          <Seccion sk={SECCIONES[6]} ctx={ctx}>
            {!afectaPersonas ? (
              <div className="text-center py-6">
                <FileWarning size={26} className="text-slate-300 mx-auto" />
                <p className="text-body text-ink-soft mt-2">
                  No aplica porque no marcaste <b>"problemas de seguridad en personas"</b> en la sección 3.
                </p>
              </div>
            ) : (
              <>
                <Alert tone="danger" title="Aviso de privacidad">
                  Estos son datos de salud de una persona. Solo los ven los roles autorizados y cada consulta
                  queda registrada. Se conservan durante el plazo que defina ILENDER; cumplido ese plazo se
                  ocultan y luego se borran de forma definitiva: el caso se conserva, pero sin identificar a la persona.
                </Alert>

                {/* ⚠ NUEVO Y NO OPCIONAL. El backend acepta `consentimiento_informado` y el
                    prototipo no lo pedia: se recogian datos de salud de una persona
                    identificable sin dejar constancia de que ella lo autorizo. La Ley 29733
                    exige consentimiento para tratar dato sensible, y "lo dijo por telefono"
                    no es constancia si no queda registrado en el expediente.

                    Va PRIMERO, antes que ningun dato de la persona, porque preguntarlo al
                    final invita a rellenar todo y marcarlo al vuelo. */}
                <Field label="¿La persona afectada autorizó el registro de sus datos?" grupo className="mt-4"
                  hint="Ley 29733. Sin autorización registrada no se deben capturar sus datos de salud">
                  <Checkbox
                    checked={Boolean(c.personas?.consentimiento)}
                    onChange={(e) => set('personas.consentimiento', e.target.checked)}
                    tone="warning"
                    label="Sí, la persona (o su representante) autorizó registrar estos datos"
                    hint="Queda constancia en el expediente, con la fecha y quién lo recogió" />
                </Field>
                {c.personas && !c.personas.consentimiento && (
                  <Alert tone="warning" title="Falta la autorización">
                    Puedes describir el evento sin identificar a la persona. Mientras no marques la
                    autorización, evita escribir iniciales, ocupación o cualquier dato que permita
                    reconocerla.
                  </Alert>
                )}

                <div className="mt-4">
                  <Field grupo label="Vía de exposición" required {...validar('personas.vias')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Se guarda `v.id` (el codigo del catalogo) y se muestra `v.label`.
                          `afectacion_via_exposicion.via` es FK a `via_exposicion_persona(codigo)`:
                          guardar la etiqueta viola la FK y el caso no llega a base. */}
                      {CATALOGOS.viaExposicionPersonas.map((v) => (
                        <Checkbox key={v.id} checked={c.personas?.vias?.includes(v.id) || false}
                          onChange={() => { tocar('personas.vias'); setC((p) => conViaAlternada(p, v.id)); }} label={v.label} />
                      ))}
                    </div>
                  </Field>
                  {/* DECIR CUAL FUE ESE "OTRO" (CP-06-13, 3-sep-2026). HU-06 escribe esa
                      fila como "Otros (especificar) | Casilla + texto": es la unica de su
                      tabla con dos controles. Marcar «Otros» sin decirlo no informa de
                      nada —no es lo mismo salpicarse que inhalarlo que una herida
                      previa— y el Comite lo necesita para evaluar causalidad.
                      Mismo patron que el sitio de inyeccion, que ya se abria asi. */}
                  {c.personas?.vias?.includes(VIA_OTROS) && (
                    <Field label="¿Cuál fue esa otra vía?" required className="mt-4"
                      {...validar('personas.viaOtrosDetalle')}
                      contador={cuentaAtras(c.personas?.viaOtrosDetalle, LIMITES.persona_via_otros_caracteres)}>
                      <Input value={c.personas?.viaOtrosDetalle || ''}
                        placeholder="ej. salpicadura en la cara al agitar el frasco"
                        maxLength={LIMITES.persona_via_otros_caracteres}
                        onChange={(e) => set('personas.viaOtrosDetalle', conTope(e.target.value, LIMITES.persona_via_otros_caracteres))} />
                    </Field>
                  )}
                  {c.personas?.vias?.includes(VIA_QUE_ABRE_SITIO) && (
                    <Field label="Sitio de la inyección" grupo className="mt-4">
                      <div className="flex flex-wrap gap-2">
                        {CATALOGOS.sitioInyeccion.map((s) => (
                          <Radio key={s.id} name="sitio" value={s.id} checked={c.personas?.sitioInyeccion === s.id}
                            onChange={(e) => {
                              // Al soltar «Otro», su detalle deja de aplicar. Mismo criterio
                              // que el sitio cuando se suelta la via de inyeccion.
                              const sitio = e.target.value;
                              setC((p) => ({ ...p, personas: { ...p.personas, sitioInyeccion: sitio,
                                sitioOtroDetalle: sitio === SITIO_OTRO ? p.personas?.sitioOtroDetalle : '' } }));
                            }} label={s.label} />
                        ))}
                      </div>
                    </Field>
                  )}
                  {/* El "Otro" del SITIO no lo pedia HU-06 —solo enumera dedo, mano,
                      articulacion, otro— pero el operador lo decidio el 3-sep-2026 por el
                      mismo motivo que el de la via: no es lo mismo pincharse en el
                      antebrazo que en el ojo. Queda escrito en HU-06. */}
                  {c.personas?.vias?.includes(VIA_QUE_ABRE_SITIO) && c.personas?.sitioInyeccion === SITIO_OTRO && (
                    <Field label="¿Cuál fue ese otro sitio?" required className="mt-4"
                      {...validar('personas.sitioOtroDetalle')}
                      contador={cuentaAtras(c.personas?.sitioOtroDetalle, LIMITES.persona_sitio_otro_caracteres)}>
                      <Input value={c.personas?.sitioOtroDetalle || ''}
                        placeholder="ej. antebrazo"
                        maxLength={LIMITES.persona_sitio_otro_caracteres}
                        onChange={(e) => set('personas.sitioOtroDetalle', conTope(e.target.value, LIMITES.persona_sitio_otro_caracteres))} />
                    </Field>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {/* `afectacion_sexo_valido` es un CHECK: solo 'masculino' o 'femenino', en
                        minusculas. Con el campo libre que habia, escribir "Masculino" o "M"
                        hacia fallar el INSERT — y el error salia al guardar, lejos de aqui. */}
                    <Field label="Sexo" grupo>
                      <div className="flex flex-wrap gap-2">
                        {CATALOGOS.sexoPersona.map((s) => (
                          <Radio key={s} name="sexoPersona" value={s} checked={c.personas?.sexo === s}
                            onChange={(e) => set('personas.sexo', e.target.value)} label={enMayuscula(s)} />
                        ))}
                      </div>
                    </Field>
                    {/* `afectacion_persona.edad` es integer con CHECK 0..120 y el API la valida
                        igual. Un campo de texto dejaba escribir "35 años" y eso es un 422. */}
                    {/* De 18 a 120 (CP-06-12). El tope de digitos pasa a 3 para que 120
                        quepa: con dos, escribir 120 dejaba 12 en silencio. NO se filtra al
                        teclear —para escribir 18 hay que pasar por el 1— sino que se avisa
                        al salir del campo y se impide enviar. */}
                    <Field label="Edad" hint="En años, de 18 a 120. Se pide edad, no fecha de nacimiento"
                      {...validar('personas.edad')}
                      contador={cuentaAtras(c.personas?.edad, LIMITES.persona_edad_digitos, 'dígitos')}>
                      <Input inputMode="numeric" value={c.personas?.edad || ''} placeholder="ej. 35"
                        onChange={(e) => set('personas.edad', soloEnteros(e.target.value, LIMITES.persona_edad_digitos))} />
                    </Field>
                    <Field label="Ocupación" contador={cuentaAtras(c.personas?.ocupacion, LIMITES.persona_ocupacion_caracteres)}>
                      <Input value={String(c.personas?.ocupacion ?? '')} disabled={!c.personas?.consentimiento}
                        maxLength={LIMITES.persona_ocupacion_caracteres}
                        onChange={(e) => set('personas.ocupacion', conTope(e.target.value, LIMITES.persona_ocupacion_caracteres))} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Field label="Dosis recibida"><Input value={String(c.personas?.dosisRecibida ?? '')} onChange={(e) => set('personas.dosisRecibida', e.target.value)} /></Field>
                    <Field label="Consecuencias y desenlace"><Input value={String(c.personas?.desenlace ?? '')} onChange={(e) => set('personas.desenlace', e.target.value)} /></Field>
                    {/* Los tres nuevos respecto del prototipo. `iniciales` se pide en vez del
                        nombre a proposito: identifica lo justo para no duplicar el caso y es lo
                        maximo que la minimizacion del dato (RNF-02) permite guardar.
                        Se bloquean sin autorizacion: son los que permiten reconocer a la persona. */}
                    <Field label="Iniciales" hint="Solo letras y puntos. No el nombre completo"
                      contador={cuentaAtras(c.personas?.iniciales, LIMITES.persona_iniciales_caracteres)}>
                      {/* Filtrar a letras y puntos es lo que hace cumplir la minimizacion:
                          en un campo que solo admite iniciales no se puede escribir el
                          nombre completo aunque se quiera. */}
                      <Input value={String(c.personas?.iniciales ?? '')} disabled={!c.personas?.consentimiento}
                        maxLength={LIMITES.persona_iniciales_caracteres} placeholder="ej. M.T.R."
                        onChange={(e) => set('personas.iniciales', soloIniciales(e.target.value, LIMITES.persona_iniciales_caracteres))} />
                    </Field>
                    {/* `afectacion_persona.atencion_medica` es boolean y el API lo declara
                        `bool | None`: CUALQUIER texto aqui era un 422 al guardar. Es Si/No, y
                        el detalle de la atencion cabe en "Qué le ocurrió a la persona". */}
                    <Field label="¿Recibió atención médica?" grupo>
                      <div className="flex flex-wrap gap-2">
                        {OPCIONES_SI_NO.map(([valor, etiqueta]) => (
                          <Radio key={valor} name="atencionMedica" value={valor}
                            checked={c.personas?.atencionMedica === valor}
                            onChange={(e) => set('personas.atencionMedica', e.target.value)} label={etiqueta} />
                        ))}
                      </div>
                    </Field>
                    {/* El texto clinico de lo que le paso a la persona. El backend lo acepta como
                        `descripcion_afectacion` y es la materia prima de la evaluacion: sin el, el
                        bloque L2 guarda edad y sexo pero no QUE ocurrio. */}
                    <Field label="Qué le ocurrió a la persona" className="md:col-span-2"
                      hint="Signos, cuándo aparecieron y cómo evolucionó"
                      contador={cuentaAtras(c.personas?.descripcion, LIMITES.persona_descripcion_caracteres)}>
                      <Textarea rows={3} value={String(c.personas?.descripcion ?? '')}
                        maxLength={LIMITES.persona_descripcion_caracteres}
                        onChange={(e) => set('personas.descripcion', conTope(e.target.value, LIMITES.persona_descripcion_caracteres))} />
                    </Field>
                  </div>
                </div>
              </>
            )}
          </Seccion>

          {/* 8 · Declaración */}
          <Seccion sk={SECCIONES[7]} ctx={ctx}>
            <Alert tone="info">
              Mediante el envío se declara que la información señalada es veraz.
            </Alert>
            <Field label="Nombre del responsable del llenado" required className="mt-4" {...validar('declaracion')}
              hint="Quien firma el Formato Verde"
              contador={cuentaAtras(c.declaracion, LIMITES.declaracion_responsable_caracteres)}>
              <Input value={c.declaracion} placeholder={usuario.nombre}
                maxLength={LIMITES.declaracion_responsable_caracteres}
                onChange={(e) => set('declaracion', soloLetras(e.target.value, LIMITES.declaracion_responsable_caracteres))} />
            </Field>
          </Seccion>

          {/* Resumen final por secciones — la comprobacion "de todo el formulario" que pide
              HU-01 antes de enviar.

              Esta SIEMPRE visible y no solo tras un envio fallido: es el sitio al que se
              vuelve cuando se ha llenado el formulario a trozos, y la pregunta que responde
              —"¿me falta algo?"— es la que lleva a pulsar enviar. Descubrirlo despues de
              pulsar es justo el recorrido que HU-01 quiere evitar.

              No repite los mensajes: eso lo hace el resumen de arriba cuando ya toca
              ensenarlos. Aqui se dice el ESTADO de cada seccion y se navega. */}
          <div className="bg-white border border-hairline rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-canvas border-b border-hairline">
              <h2 className="text-titulo font-semibold text-ink">Resumen antes de enviar</h2>
              <span className="text-meta text-ink-soft tabular-nums">
                {SECCIONES.filter((s) => !s.off && !porSeccion[s.key]?.length).length}
                {' de '}
                {SECCIONES.filter((s) => !s.off).length} secciones completas
              </span>
            </div>
            <ul className="divide-y divide-hairline">
              {SECCIONES.map((s) => (
                <FilaDelResumen
                  key={s.key}
                  s={s}
                  faltan={porSeccion[s.key]?.length || 0}
                  intentoEnvio={intentoEnvio}
                  onIr={irA}
                />
              ))}
            </ul>
            <div className="p-4 border-t border-hairline">
              <VeredictoDeEnvio completo={completo} intentoEnvio={intentoEnvio} cuantosFaltan={cuantosFaltan} />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 pb-6">
            {/* `secondary` y no `ghost` (reportado por el operador, 9-sep-2026): el
                fantasma no tiene fondo ni borde, asi que junto a "Guardar borrador" y
                "Enviar caso" se leia como texto suelto y no como accion. Sigue siendo la
                accion menos importante de las tres —por eso queda a la izquierda,
                separada del par de la derecha—, pero ya se ve que se puede pulsar. */}
            <Button variant="secondary" onClick={() => onSalir?.()}>Volver a la bandeja</Button>
            <div className="flex items-center gap-2">
              <Button variant="secondary" icon={Save} onClick={() => { onGuardar?.({ ...c, id: numeroCaso ?? null }); setGuardado(new Date()); setSinEntregar(false); }}>Guardar borrador</Button>
              <Button variant="primary" icon={Send} onClick={enviar} disabled={enviando}>Enviar caso</Button>
            </div>
          </div>
        </div>
      </div>

      <Modal open={confirmar} onClose={() => { if (!enviando) setConfirmar(false); }}
        eyebrow="Confirmación" title="¿Enviar el caso de forma definitiva?" icon={Send} tone="warning"
        footer={<>
          <Button variant="secondary" disabled={enviando} onClick={() => setConfirmar(false)}>Cancelar</Button>
          <Button variant="primary" disabled={enviando} onClick={confirmarEnvio}
            icon={enviando ? undefined : Send}>
            {enviando ? (
              // `role="status"`: el lector de pantalla anuncia que sigue trabajando. El
              // icono es decorativo y se le esconde, o leeria el nombre del icono.
              <span role="status" className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                Enviando…
              </span>
            ) : 'Sí, enviar'}
          </Button>
        </>}>
        <p>Al enviarlo, el sistema genera el expediente <Mono className="font-semibold">{numeroCaso}</Mono> y avisa
          por correo al Comité de Farmacovigilancia, a Gerencia General y a los dos VP.</p>
        <p className="text-ink-soft">Después del envío ya no podrás editar el formulario: las correcciones se
          gestionan como observaciones del caso.</p>
      </Modal>

      {/* CP-03-13/14 y CP-06-12/13/14. Cancelar deja TODO como estaba —categoría y
          datos—: es el caso que más importa de los tres, porque un diálogo que
          destruye igual al cancelar es peor que no tenerlo. */}
      <Modal
        open={Boolean(confirmarCategoria)}
        onClose={() => setConfirmarCategoria(null)}
        eyebrow="Dato sensible"
        title="¿Quitar la categoría y borrar los datos de la persona?"
        icon={TriangleAlert}
        tone="danger"
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmarCategoria(null)}>Cancelar</Button>
          <Button variant="primary" onClick={() => {
            if (confirmarCategoria) toggleCategoria(confirmarCategoria.id);
            setConfirmarCategoria(null);
          }}>
            Sí, quitar y borrar
          </Button>
        </>}>
        {/* UNA SOLA RAMA desde el 26-ago-2026. Este diálogo tenía dos: la de DESMARCAR
            —que borra el bloque L2 y sigue aquí— y la de MARCAR, que avisaba de que se
            iban a limpiar raza y estado fisiológico. Esa segunda dejó de existir cuando el
            animal y la persona pasaron a convivir en el mismo caso: marcar la categoría ya
            no destruye nada, así que no hay nada que confirmar y el diálogo no aparece. */}
        <p>
          Se va a borrar <b>{String(confirmarCategoria?.enRiesgo ?? '')}</b> de este caso.
        </p>
        <p className="text-ink-soft">
          Son datos de salud de una persona. Volver a marcarla no los recupera: habría que
          pedirlos otra vez. Lo que ya se guardó conserva su plazo de retención.
        </p>
      </Modal>
    </>
  );
}
